/**
 * Admin Reviews tab — row derivations (SPEC-admin-ops-review-view, Phase A).
 *
 * Lives outside SecureShield365.js for the reason adminContacts.js does: these
 * run top-level on EVERY AdminPage render once the fetch lands, so a single
 * throwing row unmounts every admin tab, not just this one. Keeping them here
 * means the crash classes are pinned by direct unit tests.
 *
 * Read-only throughout. Phase A displays; it never writes — no status change,
 * no date edit, no action of any kind originates in this file.
 */

// The ONE date authority (CLAUDE.md): DateOnly values are never parsed by a
// local formatter here. This module was dependency-free until the
// booking-ledger derivations needed a term-end day count, and importing the
// shared parser is strictly required over hand-rolling one — routing a
// DateOnly field through a timezone-aware parse IS the day-off bug
// utils/dates.js exists to prevent.
import { parseDateOnly } from "./dates";

// "Due this week" is a FILTER on the approaching bucket, not a second view or
// a second query (spec view 2).
export const DUE_SOON_DAYS = 7;

// Null pc_addtoengineerstracker is a REAL state, not a missing value — 183 of
// 385 tracking rows carry it. The backend already renders it as "Not set";
// this is the client-side fallback for an older payload.
export const WORKFLOW_UNSET = "Not set";

// Matches shared/review_ops.UNKNOWN_REVIEWER — an unattributed completion
// still happened, so it gets a named card rather than being dropped.
export const UNKNOWN_REVIEWER = "Unknown reviewer";

const lower = (value) => String(value ?? "").toLowerCase();

/* ── Band / horizon cards ────────────────────────────────────────────
   Backlog gets SEVERITY bands, coloured from the existing severity
   palette (Badge: critical purple, high red, medium orange — the same
   ones the customer overview uses; no new colour values here).
   Approaching gets HORIZON cards, deliberately uncoloured: a review
   coming up is not a warning, and painting it like one trains the reader
   to ignore the colours that are. */

// Band ids match shared/review_ops.SEVERITY_ORDER. `type` is the Badge
// palette key, not a colour value.
// Past due, but inside BACKLOG_GRACE_DAYS — so NOT backlog, and never a
// severity band. Its own card and its own section: these customers used
// to render nowhere at all, falling between Backlog (more than 10 days
// past) and Approaching (today onward).
export const JUST_PAST_DUE = "grace";
export const JUST_PAST_DUE_LABEL = "Just past due";

export const SEVERITY_BANDS = [
  { key: "critical", label: "Critical", type: "purple" },
  { key: "high",     label: "High",     type: "red" },
  { key: "medium",   label: "Medium",   type: "orange" },
];

/**
 * Human band ranges for the card subtitles, derived from the thresholds
 * the backend publishes so the copy can never drift from the rule.
 *
 * The Medium floor is grace + 1 — the backlog list starts at day 11
 * because days 1-10 are the grace window, so the operator's stated
 * "1-15 Medium" is 11-15 in practice. Stated, not silently reinterpreted.
 */
export function bandRangeLabel(key, meta) {
  const graceDays = meta?.backlog_grace_days ?? 10;
  const highFrom = meta?.severity_high_from ?? 16;
  const criticalFrom = meta?.severity_critical_from ?? 46;
  if (key === "critical") return `${criticalFrom}+ days`;
  if (key === "high") return `${highFrom}–${criticalFrom - 1} days`;
  if (key === "medium") return `${graceDays + 1}–${highFrom - 1} days`;
  return "";
}

/** Horizon cards for the approaching view: 7 days, 14 days, total. */
export function horizonCards(customers, meta) {
  const rows = (customers || []).filter(r => r && r.bucket === "approaching");
  const within = (n) => rows.filter(r => (r.days_until ?? Infinity) <= n).length;
  const horizon = meta?.approaching_days ?? 14;
  return [
    { key: "all", label: "Approaching", sub: `next ${horizon} days`,
      count: rows.length },
    { key: "d7", label: `Within ${DUE_SOON_DAYS} days`, sub: "due this week",
      count: within(DUE_SOON_DAYS) },
    { key: "d14", label: `Within ${horizon} days`, sub: "the full horizon",
      count: within(horizon) },
  ];
}

/** Customers in one bucket, ordered for that view.
 *
 * `severityBand` narrows the BACKLOG bucket to one band (the severity
 * cards); `dueSoonOnly` narrows APPROACHING to the 7-day cut. Each is
 * ignored on the bucket it does not apply to.
 */
/**
 * The Scheduling view (2026-08-11) merges Backlog and Approaching into one
 * list with a filter, replacing two tabs. The options and what each
 * selects:
 *
 *   backlog             the backlog bucket   (the DEFAULT — today's landing
 *                       view, and the grace section still renders below it)
 *   approaching         the approaching bucket        (today's second tab)
 *   backlogapproaching  backlog + approaching, one list, NO grace
 *   all                 backlog + grace + approaching + future + unscheduled
 *   onhold              On Hold + Pending rows, ANY bucket
 *
 * WHY "all" INCLUDES GRACE. Grace rows are past due but inside the grace
 * window, so they are in NEITHER the backlog nor the approaching bucket —
 * today they render in their own section beneath the backlog list. A union
 * of the two buckets would silently drop them (53 live rows on 2026-08-11).
 * Everything past due or coming up belongs in the combined view.
 * (The separate "Backlog + Approaching" tab IS that union and drops grace BY
 * DESIGN, on James's ask — a grace-free working list is a different tool from
 * All, not a contradiction of this rationale, which stays true for All.)
 *
 * WHY On Hold + Pending IS A FILTER AND NOT AN EXCLUSION. The default
 * universe is Ongoing + Open, but a row nobody can see is a row that rots.
 * Six live rows sit outside the default; the fourth option is how they stay
 * reachable.
 */
/**
 * Timing for ONE row, correct for every scheduling bucket — the combined
 * views put backlog, just-past-due and approaching rows in one list, and the
 * API populates exactly one of days_overdue / days_until per row, nulling
 * the other. A single boolean column therefore cannot describe them: it
 * renders null for two of the three buckets whichever field it picks.
 *
 * Returns { text, note, tone } where tone is "overdue" | "grace" | "due" —
 * a KEY, not a colour, so this module stays free of the theme. Copy comes
 * from BUCKET_LABELS / JUST_PAST_DUE_LABEL rather than being invented here.
 */
export function schedulingTiming(row) {
  if (!row) return null;
  // NO DATE IS A STATE, NOT A BLANK (2026-08-11). Once "All" became the
  // whole universe these rows render for the first time, and an em dash in
  // the timing column reads as "nothing to do" — the exact opposite of true
  // for a customer nobody has scheduled an audit for. Checked FIRST: an
  // unscheduled row has no next_audit_date, so any day count on one would be
  // a contradiction, not a timing to render.
  if (row.bucket === "unscheduled") {
    return { text: "Not scheduled", note: null, tone: "none" };
  }
  const over = row.days_overdue;
  const until = row.days_until;
  if (row.bucket === "grace" && Number.isInteger(over)) {
    return { text: `${over} d overdue`, note: JUST_PAST_DUE_LABEL, tone: "grace" };
  }
  if (Number.isInteger(over)) {
    return { text: `${over} d overdue`, note: null, tone: "overdue" };
  }
  if (Number.isInteger(until)) {
    return {
      text: until === 0 ? "Today" : `due in ${until} d`,
      note: null,
      tone: "due",
    };
  }
  return { text: "—", note: BUCKET_LABELS[row.bucket] || null, tone: "due" };
}

/**
 * Default order for a COMBINED scheduling view: most urgent first across
 * mixed buckets — most overdue, then just past due, then soonest due.
 *
 * A NEW SORT, because none existed. sortReviewRows("") is the identity
 * function and the server's order ranks grace after approaching
 * (_BUCKET_RANK: backlog 0, approaching 1, grace 2), which puts a
 * 3-day-overdue row below one due in 29 days. That ranking is right for the
 * API's own stable order and wrong for a list a human reads top-down.
 *
 * Applied ONLY when the reader has not chosen a sort; an explicit choice
 * still wins, exactly as on the focused filters.
 */
// FUTURE AND UNSCHEDULED RANK EXPLICITLY (2026-08-11), because "All" now
// carries them. They shared the `?? 3` fallback before, and the within-rank
// key is 0 for a row with no day count — so an unscheduled row outranked one
// scheduled 120 days out. A row with NO date cannot be more urgent than a
// row with one: unscheduled sorts LAST, below everything with a calendar
// date, and an unknown bucket sorts below even that.
const URGENCY_RANK = {
  backlog: 0, grace: 1, approaching: 2, future: 3, unscheduled: 4,
};

export function sortSchedulingUrgency(rows) {
  const list = [...(rows || [])].filter(Boolean);
  const rank = (r) => URGENCY_RANK[r?.bucket] ?? 5;
  const within = (r) => (
    Number.isInteger(r?.days_overdue) ? -r.days_overdue
      : Number.isInteger(r?.days_until) ? r.days_until
        : 0
  );
  list.sort((a, b) => {
    const ra = rank(a); const rb = rank(b);
    if (ra !== rb) return ra - rb;
    const wa = within(a); const wb = within(b);
    if (wa !== wb) return wa - wb;
    // Same stable tiebreak as sortReviewRows so equal keys never shuffle.
    const na = lower(a?.customer_name); const nb = lower(b?.customer_name);
    if (na !== nb) return na.localeCompare(nb);
    return lower(a?.slug).localeCompare(lower(b?.slug));
  });
  return list;
}

/** Default order for the Banked outreach view. Classification, thresholds,
 *  date arithmetic and the within-state sort facts all come from the server.
 *  A missing/old payload is treated as the server's unavailable rank so a
 *  frontend-first deploy keeps the row visible and sortable. */
export function sortBankedOutreach(rows) {
  const list = [...(rows || [])].filter(Boolean);
  const sortFact = (r, key, fallback) => {
    const value = r?.banked_outreach?.timing?.[key];
    return Number.isFinite(value) ? value : fallback;
  };
  list.sort((a, b) => {
    for (const [key, fallback] of [
      ["sort_rank", 2],
      ["sort_basis_rank", 3],
      ["sort_value", 0],
    ]) {
      const av = sortFact(a, key, fallback);
      const bv = sortFact(b, key, fallback);
      if (av !== bv) return av - bv;
    }
    const an = lower(a?.customer_name); const bn = lower(b?.customer_name);
    if (an !== bn) return an.localeCompare(bn);
    return lower(a?.slug).localeCompare(lower(b?.slug));
  });
  return list;
}

export const SCHEDULING_FILTERS = [
  "backlog", "approaching", "backlogapproaching", "banked", "all", "onhold",
];
export const SCHEDULING_DEFAULT_FILTER = "backlog";
export const SCHEDULING_BUCKETS = ["backlog", "grace", "approaching"];

/** THESE ARE WIRE LABELS, NOT DISPLAY LABELS. Read before renaming either.
 *
 *  The strings below are what the BACKEND emits on the wire, from its own
 *  hardcoded `_CONTRACT_STATUS_LABELS` map (backend/function_app.py) keyed on
 *  the Dataverse choice INTEGER. The CRM choice label for 125470003 was
 *  renamed to "One Time" in Dataverse and the wire still says "Open" —
 *  deliberately, and it did not break the portal, because the backend maps
 *  the integer and never reads the CRM label.
 *
 *  That is the CONTENT-01 pattern: the wire vocabulary is the stable one, and
 *  the rename happens at the point of display (`contractDisplayLabel`, which
 *  renders "Open" as "One Time"). The two sides are ALLOWED to differ and
 *  must be changed together — renaming the backend map alone silently empties
 *  this universe, and renaming these strings alone does the same. The
 *  KNOWN_CONTRACT_STATUS guard below turns that into a red test.
 *
 *  "Open" IS in the default universe on purpose (operator ruling
 *  2026-08-11): a One Time engagement is owed a delivery and gets chased the
 *  same way an Ongoing customer does. Only its LABEL changes. */
const DEFAULT_UNIVERSE = ["Ongoing", "Open"];
const REDUCED_UNIVERSE = ["On Hold", "Pending"];

/** Rows in the universe a Scheduling filter looks at. A row with NO
 *  contract_status is kept in both — an unknown status must not make a row
 *  invisible, which is the failure this filter exists to avoid.
 *
 *  THIS SPLIT IS ENTIRELY CLIENT-SIDE, AND IT COVERS FOUR OF SEVEN STATUSES.
 *  Read before adding server-side filtering (2026-08-11).
 *
 *  The `fetchReviewOps` reader in utils/api.js sends only start, end and
 *  today — no status or universe parameter. The whole payload arrives and
 *  the two universes above divide it here. Between them they name Ongoing,
 *  Open, On Hold and Pending. The backend's vocabulary is SEVEN values
 *  (`_CONTRACT_STATUS_LABELS`, function_app.py): Closed, Expired and New are
 *  in NEITHER universe, so a row carrying one would be counted by
 *  `bucketCounts` while appearing under NO Scheduling filter — invisible in
 *  the UI and still inflating any raw badge.
 *
 *  That is not reachable today, and the reason is server-side, not here:
 *  `_ACTIVE_TRACKING_STATUS_FILTER` (shared/dynamics_client.py) bounds the
 *  query to the same four statuses this file knows about. Nothing in the
 *  frontend states that dependency, and nothing enforces it.
 *
 *  So the hazard for whoever widens the payload — and the Phase 3 "contract
 *  ended but still delivering" work intends to, in order to surface Expired
 *  rows: widening that server filter without adding the matching universe
 *  here creates rows that no filter lists. Widening it while ALSO filtering
 *  status server-side breaks it from the other end, because every
 *  `schedulingTotal` badge would then apply the client split on top of an
 *  already-narrowed payload — double-filtering, and desyncing from the raw
 *  bucket counts that the badge fix of 2026-08-11 went to some trouble to
 *  reconcile. Change the two together, or move the universe decision to one
 *  side and delete it from the other. */
export function filterSchedulingUniverse(customers, filterKey, {
  includeOnHold = false,
} = {}) {
  // Banked is an allowance-model outreach view over the ENTIRE active
  // Review Ops universe. It deliberately ignores contract-status splitting,
  // so Banked On Hold/Pending rows remain reachable here. Only the explicit
  // normalized wire value qualifies; missing/invalid API properties are not
  // guessed Banked.
  if (filterKey === "banked") {
    return (customers || []).filter(
      r => r && r.allowance_model === "banked",
    );
  }
  // The "Include On Hold" checkbox widens the universe for the ALL view
  // only (2026-08-11) — a convenience on the whole-universe view, not a
  // replacement for the On Hold + Pending pill, which is untouched. It
  // cannot widen a focused bucket filter, and it is meaningless on onhold,
  // which is already that universe.
  const wanted = filterKey === "onhold"
    ? REDUCED_UNIVERSE
    : (filterKey === "all" && includeOnHold)
      ? [...DEFAULT_UNIVERSE, ...REDUCED_UNIVERSE]
      : DEFAULT_UNIVERSE;
  return (customers || []).filter(r => {
    if (!r || (r.contract_status && !wanted.includes(r.contract_status))) {
      return false;
    }
    // Explicit Banked rows are not backlog claims. They stay in All (whose
    // contract universe is unchanged) and move out of every focused
    // scheduling/hold filter. Missing/invalid allowance_model values remain
    // in their ordinary contract/bucket view during deploy or cache skew.
    return filterKey === "all" || r.allowance_model !== "banked";
  });
}

/** Rows the merged Scheduling view shows for one filter, before search,
 *  readiness, expired, unrecorded and sort are applied.
 *
 *  "ALL" MEANS THE WHOLE UNIVERSE (2026-08-11), EVERY BUCKET. It restricted
 *  to SCHEDULING_BUCKETS until now, which read ~176 against a universe of
 *  ~275 and left every future and unscheduled row listed by no filter at
 *  all — counted by `bucketCounts`, invisible in the UI. Those rows were
 *  already in the payload, so this is a filter change and not a data change.
 *  The rows arriving with no meaningful timing are why `schedulingTiming`
 *  renders "Not scheduled" rather than a blank.
 *
 *  ONHOLD IS DELIBERATELY UNCHANGED — still the three scheduling buckets.
 *  It is a focused triage list, not a second whole-universe view. */
export function filterScheduling(customers, filterKey, {
  dueSoonOnly = false, severityBand = "", includeOnHold = false,
} = {}) {
  const universe = filterSchedulingUniverse(customers, filterKey, { includeOnHold });
  if (filterKey === "backlog") {
    return filterByBucket(universe, "backlog", { severityBand });
  }
  if (filterKey === "approaching") {
    return filterByBucket(universe, "approaching", { dueSoonOnly });
  }
  if (filterKey === "backlogapproaching") {
    // Amber's focused working list: the two buckets she works, in one tab.
    // ONE predicate over the single scalar `bucket` — NOT two concatenated
    // filter passes, which would double-count the day the field stops being
    // scalar. Grace is its OWN bucket value, so grace-bucketed rows (1-10
    // days past due, calendar or working days by status) were never in this
    // set — excluded by construction, not by a filter. No cards apply, so no
    // card filters are honoured, exactly as on "all".
    return universe.filter(
      r => r && (r.bucket === "backlog" || r.bucket === "approaching"),
    );
  }
  if (filterKey === "banked") {
    // Every explicit Banked row, every bucket. Suppression remains a separate
    // visible section at the component boundary, preserving the existing
    // actionable + suppressed identity without hiding the row.
    return universe;
  }
  if (filterKey === "all") {
    // Every bucket, including future and unscheduled. No cards apply, so no
    // card filters are honoured.
    return universe;
  }
  // onhold: the buckets that need scheduling attention, in the server's order.
  return universe.filter(r => SCHEDULING_BUCKETS.includes(r.bucket));
}

/** Row copy for a blocking touchpoint — BOTH numbers, never one
 *  (SPEC-blocking-touchpoint-suppression): "access meeting Aug 19" sits
 *  beside the day count, not instead of it. A suppressed row is still 94
 *  days overdue and the reader must see both facts at once, or the
 *  annotation reads as an excuse rather than a status.
 *
 *  `fmtDate` is injected (utils/dates.fmtDateOnly) — never a local
 *  formatter, per the date-authority rule. */
export function blockingLabel(row, fmtDate) {
  const raw = row?.blocked_until_date;
  if (!raw) return null;
  const when = fmtDate ? fmtDate(raw) : raw;
  if (!when) return null;
  const kickoff = String(row?.workflow_status_value) === "125470013";
  return row?.blocking_lifted
    // The lift marker: derived from a fact that has already happened, so it
    // states the completed fact rather than prescribing a next step.
    ? (kickoff ? "kickoff done" : "access meeting done")
    : (kickoff ? `Kickoff Scheduled ${when}` : `access meeting ${when}`);
}

/** The rebook marker's line, or null — "redo requested — send a new
 *  booking link".
 *
 *  SERVER-DERIVED, read not recomputed (review_ops.rebook_needed). The
 *  rule reads the review-type vocabulary and the tracker choice integers,
 *  and both of those belong to the backend; a client-side copy would be a
 *  second place to update when either moves. Exactly the split
 *  isBlockingSuppressed already uses, for exactly that reason.
 *
 *  BOTH NUMBERS, NEVER ONE (the blockingLabel rule): this renders BESIDE
 *  the day count, not instead of it. A row needing a rebook is still 40
 *  days overdue, and replacing the count with the annotation would make
 *  the marker read as an excuse rather than a next step.
 *
 *  ONE VARIANT ONLY (operator ruling 2026-08-13). A rescheduling no-show
 *  does NOT raise this: its notice email already carried the new booking
 *  link, so the line would ask the team to do a thing the system just did.
 *  The exclusion lives server-side; this function renders whatever the
 *  flag says, and the flag is the single statement of the rule. */
export const REBOOK_LABEL = "reschedule requested — send a new booking link";

/** The READINESS PILL's word for the same flag — one word, because it
 *  shares a narrow column with pills like "Ready to book". The long line
 *  above still renders beside the day count; this is the column Amber
 *  actually scans. Both strings live here so the two cannot drift. */
export const REBOOK_PILL_LABEL = "Reschedule";

export function rebookLabel(row) {
  return row?.rebook_needed ? REBOOK_LABEL : null;
}

/** Is this row suppressed by a blocking touchpoint scheduled in the
 *  FUTURE? Server-derived per request (review_ops.blocking_suppressed) and
 *  never recomputed here — the day count must not be cached, and the
 *  frontend has no business re-deriving a date comparison the API already
 *  did against the caller's `today`. */
export function isBlockingSuppressed(row) {
  return !!row?.blocking_suppressed;
}

/** Rows in a filter that are ACTIONABLE — overdue with nothing scheduled.
 *
 *  THE PRIMARY NUMBER (SPEC-blocking-touchpoint-suppression). A backlog of
 *  50 is not an actionable number if 20 of those customers have already
 *  committed to a meeting. Suppressed rows are excluded from every count
 *  the page presents as "the backlog", and get their own count instead.
 *
 *  Actionable = in this filter's universe AND not suppressed. Bucket,
 *  day count and severity are untouched — suppression annotates. */
export function filterActionable(customers, filterKey, opts = {}) {
  return filterScheduling(customers, filterKey, opts)
    .filter(r => !isBlockingSuppressed(r));
}

/** Rows in a filter that ARE suppressed — the other half of the split. */
export function filterSuppressed(customers, filterKey, opts = {}) {
  return filterScheduling(customers, filterKey, opts).filter(isBlockingSuppressed);
}

/** The denominator for "showing N of M" — the ACTIONABLE size of the
 *  universe this filter looks at (2026-08-11).
 *
 *  It counted every row in the universe until blocking touchpoints landed.
 *  The suppressed rows are not lost: suppressedTotal counts them and the
 *  section beneath the table shows them, so the split is on one screen and
 *  no number is lying. THE IDENTITY actionable + suppressed == total is
 *  asserted as a test, because two things that count the same quantity
 *  disagree unless something forbids it (#532). */
export function schedulingTotal(customers, filterKey, opts = {}) {
  return filterActionable(customers, filterKey, opts).length;
}

/** How many rows this filter is holding back on a customer meeting. */
export function suppressedTotal(customers, filterKey, opts = {}) {
  return filterSuppressed(customers, filterKey, opts).length;
}

/** Every row in the filter's universe, suppressed or not — the total the
 *  identity is checked against. Focused backlog badges remain actionable;
 *  Banked alone uses this total because its pill opens both visible sections. */
export function schedulingUniverseTotal(customers, filterKey, opts = {}) {
  return filterScheduling(customers, filterKey, opts).length;
}

export function filterByBucket(customers, bucket, {
  dueSoonOnly = false, severityBand = "", horizonDays = null,
} = {}) {
  let rows = (customers || []).filter(r => r && r.bucket === bucket);
  if (bucket === "backlog" && severityBand) {
    rows = rows.filter(r => r.severity === severityBand);
  }
  if (bucket === "approaching") {
    if (dueSoonOnly) {
      rows = rows.filter(r => (r.days_until ?? Infinity) <= DUE_SOON_DAYS);
    } else if (horizonDays !== null && horizonDays !== undefined) {
      rows = rows.filter(r => (r.days_until ?? Infinity) <= horizonDays);
    }
  }
  return rows;
}

/**
 * Contact / login diagnostics for one row, as {label, tone}.
 *
 * NOT CURRENTLY RENDERED. The Contacts and "Signed in" columns came off
 * the Backlog and Approaching lists on 2026-08-08 — they are not what a
 * person acts on from those lists, and the rows were eleven columns
 * wide. These helpers, their tests, and the active_contacts /
 * ever_logged_in / last_login fields on the payload are all RETAINED
 * deliberately: the data is RELOCATING to a Customer Engagement page,
 * not retiring. Delete none of it without that page being cancelled.
 *
 * Zero is a FINDING, not missing data, so it renders as an explicit
 * phrase in a warning tone rather than a 0 or a blank:
 *   no contacts  → nobody to email or book. A CRM problem.
 *   never signed in → contacts exist but nobody has engaged. A different
 *   problem, needing a different person.
 */
export function contactDiagnostic(row) {
  const count = row?.active_contacts;
  if (count === null || count === undefined) return { label: "—", tone: "muted" };
  if (count === 0) return { label: "No contacts", tone: "warn" };
  return { label: `${count}`, tone: "text" };
}

export function loginDiagnostic(row) {
  if (row?.ever_logged_in) return { label: "Yes", tone: "text" };
  if (!row?.active_contacts) {
    // No contacts is already reported by its own column; repeating
    // "never" here would read as a second, separate failure.
    return { label: "—", tone: "muted" };
  }
  return { label: "Never signed in", tone: "warn" };
}

/**
 * Free-text narrowing across the fields a reader can actually see: customer
 * name, slug, tier, workflow status and last reviewer. Matching on the
 * reviewer is deliberate — "everything Pablo last touched" is a question
 * Amber asks of the approaching list.
 */
export function searchRows(rows, query) {
  const needle = lower(query).trim();
  if (!needle) return rows || [];
  return (rows || []).filter((r) => {
    if (!r) return false;
    return [
      r.customer_name,
      // BOTH names. The row is labelled with the tracking name, but
      // someone who knows the customer as "Convey Health" from CRM must
      // still find "Convey Health Solutions" — and vice versa.
      // Displaying one name while searching only the other would make a
      // third of the fleet HARDER to find, not easier.
      r.customer_record_name,
      r.slug,
      r.tier,
      r.workflow_status,
      // BOTH the wire label and the displayed one, for the same reason as
      // the two customer names above: the column says "One Time" and the
      // wire says "Open", so matching only the wire makes the label a
      // reader can SEE unsearchable.
      r.contract_status,
      contractDisplayLabel(r.contract_status),
      r.last_review?.reviewer_name,
      // The Customer Manager (Dataverse: pc_patriotaccountexecutive). Only
      // the Customer Management payload carries it, so on Scheduling and
      // Delivery this term is simply absent and matches nothing — the same
      // no-op the tier and contract fields already are on payloads that
      // omit them. One query string means the same thing in every view.
      r.customer_manager,
    ].some(field => lower(field).includes(needle));
  });
}

/**
 * Cycle position as "Y2 R3", or null when the customer is not on a cycle.
 * BOTH fields must be populated — the same on_cycle rule the write side uses
 * (shared/review_cycle.on_cycle), so the two never disagree about who is on a
 * cycle. Half a position is not a position. Banked rows are the exception:
 * their review position is a date/window fact, so Review Number is deliberately
 * not rendered as the allowance authority.
 */
export function cycleLabel(row) {
  if (!row) return null;
  const { contract_year: year, review_number: number } = row;
  if (row.allowance_model === "banked") {
    if (year === null || year === undefined) return "Banked";
    return `Y${year} · Banked`;
  }
  if (year === null || year === undefined) return null;
  if (number === null || number === undefined) return null;
  return `Y${year} R${number}`;
}

/**
 * Last-review summary line, or the day-one sentinel. Never an empty string —
 * a blank cell reads as a data problem, which is exactly what this is not
 * (spec view 2).
 */
export function lastReviewLabel(row, { fmtDate } = {}) {
  const last = row?.last_review;
  if (!last || !last.review_date) return "No recorded reviews";
  const when = fmtDate ? fmtDate(last.review_date) : last.review_date;
  const who = last.reviewer_name || UNKNOWN_REVIEWER;
  // The DISPLAYED type: "Scheduled" reads "Completed" here too (2026-08-12)
  // — Amber asked for everywhere, and this column is the Scheduling view's
  // record of finished work.
  const type = reviewTypeDisplayLabel(last.review_type);
  const what = type ? ` · ${type}` : "";
  return `${when} · ${who}${what}`;
}

/** Workflow status for display, with the "Not set" fallback. */
/**
 * Workflow-status colour FAMILY for a row, as a tone key. The component maps
 * keys to colours; this module never holds a hex value.
 *
 * COLOUR ENCODES MEANING; THE CONTROL ENCODES WRITABILITY. The two are
 * orthogonal and conflating them is what this register exists to avoid.
 * "Not Started" reads as normal rather than disabled because it is the
 * resting state of most rows — and that was true even while it was
 * unwritable, which is the point: the colour never tracked writability.
 * (It became writable by hand on 2026-08-13; the Review Complete button
 * still stamps it too. Its tone did not change.) "Completed" and "Void"
 * are muted because NOBODY SELECTS THEM, not because they are unwritable.
 * Whether a value can be SET is the dropdown's job (it offers only
 * STATUS_DATE_RULE keys), and that is a different question from what the
 * value MEANS on a row.
 *
 * MAPPED BY MEANING, NOT BY ROUTE. Access Issue - Legacy is BLOCKED, the
 * same class as Prerequisite Check Issue, even though one runs on the legacy
 * collection route and the other does not. Which collector runs a row is
 * already shown in the Scan method column; the status column says what is
 * HAPPENING.
 *
 * The four "- Legacy" statuses are LIVE, not historical residue —
 * review_ops.LEGACY_ROUTE_STATUSES states it directly, and their labels keep
 * the suffix for the reason stated there: "it distinguishes the old
 * collection route from SecureScan365, and that distinction is the point."
 *
 * AMBER MEANS EXACTLY ONE THING HERE: blocked, waiting on something outside
 * our control. Two blocked states sharing it is the clarity working, not a
 * collision. (The Scan method column also uses amber, for the legacy
 * collection tool — a different column in a different cell, and the reason
 * "amber in the STATUS column" is unambiguous.)
 *
 * Patriot Red #8B1A1C appears nowhere in this register. It stays the
 * contract-expired pill's alone.
 */
// EXISTING TONES ONLY. The five below (resting/inmotion/confirmed/blocked/
// terminal) are the whole vocabulary and the 2026-08-13 status pack adds no
// sixth: the pink exception granted on that call was for the READINESS pill,
// not for workflow statuses. Every new status maps onto a tone that already
// carries its meaning — "we have asked, waiting on them" is inmotion whether
// the ask was a kickoff time or a booking link.
const WORKFLOW_TONE_BY_VALUE = {
  125470008: "resting",     // Not Started
  // ── The 2026-08-13 status pack ──
  125470012: "inmotion",    // Kickoff Times Sent — asked, waiting on them
  125470013: "confirmed",   // Kickoff Scheduled — a date exists (blocking)
  125470014: "inmotion",    // Access Times Sent — asked, waiting on them
  125470015: "inmotion",    // Passed Prerequisite — moving, not yet booked
  125470004: "inmotion",    // Booking Sent — asked, waiting on the customer
  125470005: "confirmed",   // Meeting Scheduled
  125470011: "confirmed",   // Access Meeting Scheduled
  125470006: "confirmed",   // Prebooked (CRM: "Prebook - No Reschedule")
  125470000: "inmotion",    // Initiated - Legacy
  125470007: "inmotion",    // Credentials Requested - Legacy
  125470010: "inmotion",    // Report Writing - Legacy
  125470003: "blocked",     // Prerequisite Check Issue
  125470009: "blocked",     // Access Issue - Legacy
  125470001: "terminal",    // Completed — being retired; never written
  125470002: "terminal",    // Void — a data state, not a workflow step
};

export function workflowTone(row) {
  const value = row?.workflow_status_value;
  if (value === null || value === undefined) return "unset";
  // An unrecognised integer is metadata drift, not a state — it renders as
  // the unset tone rather than being coloured as something it is not.
  return WORKFLOW_TONE_BY_VALUE[value] || "unset";
}

export function workflowLabel(row) {
  if (!row) return WORKFLOW_UNSET;
  if (row.workflow_status) return row.workflow_status;
  // Backend maps null → "Not set" and an UNRECOGNISED integer → null. Keep
  // those distinct here too: an unknown option is a metadata drift signal,
  // not a real state, so it renders as a dash.
  return row.workflow_status_value === null
    || row.workflow_status_value === undefined
    ? WORKFLOW_UNSET
    : "—";
}

export function workflowDateLine(row, fmtDate) {
  if (typeof fmtDate !== "function") return null;
  const value = row?.workflow_status_value;
  const raw = value === 125470011 ? row?.blocked_until_date
    : value === 125470014 ? row?.workflow_status_set_on : null;
  if (typeof raw !== "string" || !raw.trim()) return null;
  const rendered = fmtDate(raw);
  return rendered && rendered !== "—"
    ? `${value === 125470011 ? "Scheduled" : "Sent"} ${rendered}` : null;
}

/* ── The Scan Queue — the rows waiting on the delivery engineers ──────
 *
 * NAMED BY THE WORK, NOT THE AUDIENCE (operator design 2026-08-13). The
 * engineers needed one place listing every customer waiting on THEM, and
 * the two headings are the actions they take: fix the access problem, or
 * run the scan and publish it.
 *
 * MEMBERSHIP IS TWO WORKFLOW STATUS INTEGERS AND NOTHING ELSE. [V] Both
 * come from the metadata capture, never from a UI label or a doc:
 * docs/reconstruction/metadata/pc_secureshieldtracking.picklist-attrs.json
 * (2026-08-13) — pc_addtoengineerstracker carries 125470003 "Prerequisite
 * Check Issue" and 125470015 "Passed Prerequisite". Both are already in
 * WORKFLOW_TONE_BY_VALUE above; the integers are restated here rather than
 * derived from a tone, because "blocked" is a tone THREE statuses share and
 * membership in this queue is a fact about two specific statuses.
 *
 * NO UNIVERSE FILTER, DELIBERATELY. Scheduling splits the fleet into
 * Ongoing+Open and On Hold+Pending (filterSchedulingUniverse) because those
 * views answer "who do I book". This one answers "who is waiting on me",
 * and a customer parked in a prerequisite failure is waiting on the
 * engineers whatever their contract status says. Filtering by contract here
 * would hide exactly the rows this queue exists to stop hiding — the
 * "counted by bucketCounts, listed by no filter" failure that file warns
 * about. The contract column is on every row, so an On Hold row is visible
 * AS On Hold rather than invisible.
 *
 * ONE STATEMENT OF THE RULE. Value, heading and empty state travel together
 * in this list, so a section cannot exist without a status, a status cannot
 * appear under two headings, and the count, the list and the emptiness of
 * every section are all derived from the same predicate.
 */
export const SCAN_QUEUE_SECTIONS = Object.freeze([
  Object.freeze({
    value: 125470003,          // Prerequisite Check Issue
    label: "Fix access",
    // An empty queue is GOOD NEWS and has to read that way. "No rows"
    // reads as a broken fetch; this reads as a clear desk.
    empty: "No access issues waiting",
  }),
  Object.freeze({
    value: 125470015,          // Passed Prerequisite
    label: "Run & publish",
    empty: "Nothing waiting for a scan",
  }),
]);

const SCAN_QUEUE_VALUES = SCAN_QUEUE_SECTIONS.map(s => s.value);

/** The chip's words, on Amber's Scheduling row. Lower case and quiet on
 *  purpose: it annotates a row, it does not label a state the way the
 *  Workflow status column does — and that column is already saying which
 *  of the two statuses it is. */
export const WITH_ENGINEERS_LABEL = "with engineers";

/** Is this row sitting with the delivery engineers?
 *
 *  DERIVED FROM THE WORKFLOW STATUS, WHICH IS WHY THE CHIP IS SELF-CLEARING.
 *  There is no stored "with engineers" flag anywhere — the moment the status
 *  changes, this returns false and the chip is gone on the next render. That
 *  property is what makes PR 2's automatic status writes safe to add: they
 *  cannot leave a stale marker behind, because there is no marker to leave.
 *
 *  Null is a real state ("Not set"), never a member. An unrecognised integer
 *  is metadata drift and is not a member either — the same treatment
 *  workflowTone gives it. */
export function isWithEngineers(row) {
  const value = row?.workflow_status_value;
  return value !== null && value !== undefined
    && SCAN_QUEUE_VALUES.includes(value);
}

/** How long this row's workflow status has been set, in whole days, or
 *  null when nobody knows.
 *
 *  SERVER-COMPUTED, READ NOT DERIVED. The number comes from
 *  `workflow_status_age_days` on the payload, which the backend computes
 *  from pc_workflowstatusseton against the caller's `today`. This function
 *  does no date arithmetic and must never start: the date-authority rule
 *  puts every DateOnly calculation on one side, and the whole point of the
 *  column being TimeZoneIndependent is that exactly one place subtracts.
 *
 *  NULL IS THE COMMON CASE AT FIRST, and permanently for rows nobody
 *  touches: the stamp only exists on writes made after the 2026-08-13
 *  deploy. Null means "we do not know", never zero — collapsing the two
 *  would date every untouched row to today. */
export function workflowStatusAgeDays(row) {
  const value = row?.workflow_status_age_days;
  // Number.isInteger rejects null, undefined, NaN and "6" in one test. A
  // string that slipped through would render as "6d" and sort as garbage.
  return Number.isInteger(value) ? value : null;
}

/** The age as it is written on a row, or null when there is none.
 *
 *  ZERO READS "today", NOT "0d" (decision 2026-08-13). The timing cell one
 *  line above already renders "Today" for a due-today row
 *  (schedulingTiming), so this is the vocabulary the reader is standing in
 *  rather than a new one — and "0d" invites being read as a missing value
 *  in a column where missing values are genuinely common.
 *
 *  Negative ages are treated as unknown rather than rendered. They should
 *  not occur — the stamp is bound-checked to +/-1 day of the server date —
 *  but a clock-skewed client asking for "in -1 days" would be nonsense on
 *  screen, and this is display code, not the place to argue about it. */
export function workflowAgeLabel(row) {
  const days = workflowStatusAgeDays(row);
  if (days === null || days < 0) return null;
  return days === 0 ? "today" : `${days}d`;
}

/** The rows in ONE queue section, OLDEST FIRST, unknown ages last.
 *
 *  THE SORT ARRIVED WITH THE AGE (2026-08-13). It could not exist before:
 *  PR #554 shipped this list in the server's order because no column
 *  recorded when a status was set, and ranking by age would have meant
 *  ranking by something that wasn't age. pc_workflowstatusseton is that
 *  column, so the queue can now answer "who has been waiting longest".
 *
 *  UNKNOWN AGE SORTS LAST, never first. A row with no stamp has been
 *  waiting an unknown amount of time, and the reasonable reading of
 *  unknown is not "longest". Putting nulls on top would hand the whole
 *  pre-deploy fleet the most urgent position on day one — which is every
 *  row, and would make the sort useless exactly when it is newest.
 *
 *  STABLE WITHIN A TIE, and therefore fully degenerate to the server's
 *  order when the field is null fleet-wide: Array.prototype.sort is
 *  required to be stable, so equal keys keep their input order and the
 *  day-one list is byte-identical to what #554 shipped.
 *
 *  THE SORT IS ONE FUNCTION (sortByWaitingAge), shared with the three-pill
 *  buckets so a Prereq-issues or In-progress list ranks identically to the
 *  section it replaced — and so "oldest first" is stated once, not copied. */
export function sortByWaitingAge(rows) {
  return (rows || []).slice().sort((a, b) => {
    const ax = workflowStatusAgeDays(a);
    const bx = workflowStatusAgeDays(b);
    if (ax === null && bx === null) return 0;   // stable: server order
    if (ax === null) return 1;                  // unknown last
    if (bx === null) return -1;
    return bx - ax;                             // oldest (largest) first
  });
}

export function scanQueueRows(customers, statusValue) {
  return sortByWaitingAge((customers || []).filter(
    r => r && r.workflow_status_value === statusValue));
}

/** The whole queue's size — the OLD tab badge, and still the count behind
 *  the Scheduling "with engineers" chip's universe. THE SUM OF THE TWO
 *  STATUS SECTIONS by construction (#532).
 *
 *  NO LONGER THE SCAN QUEUE TAB BADGE. As of the three-pill restructure the
 *  tab badge is scanQueueBadgeTotal, which also counts held scans; this
 *  function keeps its old meaning (rows AT one of the two engineer statuses)
 *  and is left alone because isWithEngineers — which it is built on — also
 *  drives the Scheduling chip, and widening it would put that chip on rows
 *  that are not with the engineers (design §7b). */
export function scanQueueTotal(customers) {
  return (customers || []).filter(isWithEngineers).length;
}

// ── The three Scan Queue pills (F3 / #557) ───────────────────────────────
//
// The stacked "Fix access" / "Run & publish" sections became three filter
// pills — Prereq issues / In progress / Held for review — on 2026-08-19
// (prereq-auto-scan PR 2). The membership rule still lives in ONE place: the
// two integers below plus the held-scan list, so a pill cannot exist without
// a source list, a count is never a second derivation of the same quantity,
// and the tab badge is the three lengths summed BY CONSTRUCTION.

/** The two "with engineers" statuses the pills are named after. The integers
 *  come from the metadata capture
 *  (docs/reconstruction/metadata/pc_secureshieldtracking.picklist-attrs.json,
 *  2026-08-13), written out here rather than reached through
 *  SCAN_QUEUE_SECTIONS so a reader sees the membership rule without chasing
 *  the section table, and a metadata rename is a red test rather than an
 *  empty pill. */
export const PREREQ_ISSUE_STATUS = 125470003;    // Prerequisite Check Issue
export const PASSED_PREREQ_STATUS = 125470015;   // Passed Prerequisite

/** The lowercased join key used on BOTH sides of the held-scan match.
 *  Coerces non-strings (a numeric id that slipped through a fallback chain
 *  would otherwise throw on .toLowerCase — the pc_ssclientid lesson) and
 *  maps null/undefined to "" so a slugless held pointer never collides with
 *  a slugless row. */
function scanQueueSlugKey(slug) {
  return slug == null ? "" : String(slug).toLowerCase();
}

/** The three Scan Queue buckets, computed ONCE from the fleet payload and
 *  the held-scan list, so the pill count, the list each pill opens and the
 *  tab badge are all the same three arrays (#557 / #532).
 *
 *    prereqIssues  — every row at Prerequisite Check Issue (125470003).
 *    inProgress    — rows at Passed Prerequisite (125470015) with NO held
 *                    scan yet: the passed-prereq rows still waiting for the
 *                    auto-run to land in the queue.
 *    heldForReview — every held-scan pointer (one row per held scan; a
 *                    customer can hold more than one).
 *
 *  DISJOINT BY CONSTRUCTION, not by agreement: inProgress is a set
 *  difference against the very list heldForReview renders, so no PASSED row
 *  can appear under both In progress and Held for review. (prereqIssues and
 *  inProgress are already disjoint by status.)
 *
 *  THE JOIN KEY IS LOWERCASED ON BOTH SIDES. Held pointers carry `slug` from
 *  the blob path prefix; fleet rows carry it from Dataverse. Blob paths are
 *  case-sensitive and the marker writer stamps the canonical Dataverse-cased
 *  slug, so a mixed-case slug would otherwise leave a passed-prereq row that
 *  already has a held scan showing under BOTH pills. Lowercasing both is the
 *  one guard at the single silent-drop point (design §6). */
export function scanQueueBuckets(customers, pending) {
  const heldForReview = (pending || []).filter(Boolean);
  const heldSlugs = new Set(
    heldForReview.map(p => scanQueueSlugKey(p.slug)).filter(Boolean));
  const rows = (customers || []).filter(Boolean);
  // Oldest-first, the same rank the two stacked sections used — the pill is a
  // filter over the same list, not a new order. heldForReview keeps the
  // server's order (the pending list is date-ordered where it is built).
  const prereqIssues = sortByWaitingAge(rows.filter(
    r => r.workflow_status_value === PREREQ_ISSUE_STATUS));
  const inProgress = sortByWaitingAge(rows.filter(
    r => r.workflow_status_value === PASSED_PREREQ_STATUS
      && !heldSlugs.has(scanQueueSlugKey(r.slug))));
  return { prereqIssues, inProgress, heldForReview };
}

/** The Scan Queue tab badge — the three bucket lengths summed, from the one
 *  scanQueueBuckets call, so the number on the control is the size of the
 *  group it opens BY CONSTRUCTION rather than by a second count agreeing
 *  with the first (#532 / #557).
 *
 *  READS HIGHER THAN scanQueueTotal DID, for real rows: a held scan can
 *  belong to a customer at ANY workflow status (a Patriot manual send, a
 *  customer-initiated scan for someone on Booking Sent), so the held count
 *  is not a subset of the two engineer statuses. That widening is intended
 *  (design §7b), which is why this is a new function rather than a change to
 *  scanQueueTotal. */
export function scanQueueBadgeTotal(customers, pending) {
  const b = scanQueueBuckets(customers, pending);
  return b.prereqIssues.length + b.inProgress.length + b.heldForReview.length;
}

/** The words for a refused auto-scan, keyed by the closed code set the
 *  backend writes onto `auto_scan.reason` (design §5d; the codes are
 *  blob_client.AUTO_SCAN_REFUSAL_CODES).
 *
 *  THE FRONTEND OWNS THE WORDS. The backend sends only a code, never a
 *  sentence, so an unknown or absent code renders nothing rather than
 *  leaking a raw string to screen — the same discipline the prereq
 *  reason-code labels already use.
 *
 *  `refused` IS TERMINAL per prereq outcome (REVIEW AR-3). The auto path
 *  never retries a refusal; the next attempt is a fresh passing prereq check
 *  or an engineer's manual start. This line reports that settled fact — it
 *  is not a spinner and must not read as "retrying". `cap` and `in_flight`
 *  are reserved codes the trigger does not emit today (design §4b) but are
 *  worded here so a future seat that emits them needs no UI change. */
export const AUTO_SCAN_REFUSAL_LABELS = Object.freeze({
  cap: "scan not started — this tenant was scanned in the last 24 hours",
  in_flight: "scan not started — a scan is already running for this tenant",
  no_tenant: "scan not started — no tenant on file for this customer",
  no_scan_account: "scan not started — no scanning account configured",
  engine_error: "scan not started — the scan engine did not accept the run",
});

/** The refusal line for an In-progress row, or null when the row carries no
 *  refused auto-scan fact. Null is the answer for every non-refusal state —
 *  no `auto_scan` at all (older backend, or no claim yet), a claim still
 *  `claimed` or `started`, and an unknown reason code — because none of them
 *  is a refusal the reviewer can act on, and a guessed reason is worse than
 *  none (design §5d fail-soft, in the same direction as everything else on
 *  this path). */
export function autoScanRefusalLabel(row) {
  const auto = row && row.auto_scan;
  if (!auto || auto.status !== "refused") return null;
  return AUTO_SCAN_REFUSAL_LABELS[auto.reason] || null;
}

/** The "Just past due" card's subtitle range, from the same threshold
 *  the backlog rule uses — never a hardcoded 10. */
export function justPastDueRangeLabel(meta) {
  const graceDays = meta?.backlog_grace_days ?? 10;
  const workingDays = meta?.backlog_grace_working_days;
  // Two rules on one list (2026-08-10) — the label has to say so, or the
  // section claims a window that half its rows do not use.
  if (Number.isInteger(workingDays)) {
    return `1–${graceDays} days, or ${workingDays} working days on follow-up statuses`;
  }
  return `1–${graceDays} days`;
}

/** Per-bucket counts for the sub-view chips. */
export function bucketCounts(customers) {
  const counts = {
    backlog: 0, approaching: 0, grace: 0, future: 0, unscheduled: 0,
    dueSoon: 0,
  };
  for (const row of customers || []) {
    if (!row || !(row.bucket in counts)) continue;
    counts[row.bucket] += 1;
    if (row.bucket === "approaching"
        && (row.days_until ?? Infinity) <= DUE_SOON_DAYS) {
      counts.dueSoon += 1;
    }
  }
  return counts;
}

/**
 * Which empty-state message a range-scoped view should show. Three DISTINCT
 * states — "no rows" is not one message (spec: Day one).
 *
 *   dayOne     nothing has ever been recorded through the portal
 *   emptyRange rows exist, just none in the selected dates
 *   populated  render the table
 */
export function rangeEmptyState({ anyCompletionsRecorded, rowCount }) {
  if (rowCount > 0) return "populated";
  return anyCompletionsRecorded ? "emptyRange" : "dayOne";
}

/* ── Recorded reviews — the list, not the aggregate ──────────────────
   Three reviewers and a count each is a three-row table with nothing
   actionable in it. The reviews themselves answer both questions from one
   view: "what happened this week" is the default order, and "what did this
   engineer do" is a click on that engineer's card, or a sort on the
   reviewer column. The cards and the list are ONE interaction — the cards
   are the filter control, not a second display of the same numbers. */

/** Engineer-card selection + free-text narrowing over the visible columns. */
export function filterCompletions(completions, { reviewer, query } = {}) {
  let rows = completions || [];
  if (reviewer) {
    rows = rows.filter(r =>
      (r?.reviewer_name || UNKNOWN_REVIEWER) === reviewer);
  }
  const needle = lower(query).trim();
  if (!needle) return rows;
  return rows.filter(r => r && [
    r.customer_name,
    // BOTH names here too — the Recorded list resolves customer names
    // the same way the fleet rows do, so someone searching the CRM name
    // must find the row labelled with the tracking name.
    r.customer_record_name,
    r.reviewer_name, r.review_date,
    // BOTH the Dataverse label and the DISPLAYED one, the same reason the
    // two customer names are both here and the same rule the Contract
    // column follows: the cell says "Completed" and the wire says
    // "Scheduled", so matching only the wire makes the word a reader can
    // SEE unsearchable.
    r.review_type, completionTypeLabel(r), r.note,
  ].some(field => lower(field).includes(needle)));
}

/**
 * Sort by one column. Null sort = the server order (review date desc,
 * then createdon desc) — "what happened this week", which is the default
 * question the view answers.
 *
 * Dates sort as strings deliberately: they are bare YYYY-MM-DD, which is
 * already lexicographically ordered, so no Date object is constructed and
 * no timezone can shift a boundary day (the date-authority rule).
 */
export function sortCompletions(completions, sort) {
  const rows = [...(completions || [])];
  if (!sort || !sort.key) return rows;
  const dir = sort.dir === "desc" ? -1 : 1;
  const pick = (r) => {
    if (!r) return "";
    if (sort.key === "review_date") return r.review_date || "";
    if (sort.key === "customer_name") return lower(r.customer_name);
    if (sort.key === "reviewer_name") return lower(r.reviewer_name);
    // Sorted on the DISPLAYED label (2026-08-12), the sortLicenseProducts
    // rule: users sort what they can see, so a row reading "Completed" files
    // under C and never under S for the wire's "Scheduled".
    if (sort.key === "review_type") return lower(completionTypeLabel(r));
    // The cycle position, sorted on the DISPLAYED text so off-cycle rows
    // group together instead of scattering. "" sorts first, which puts the
    // positionless rows in one block at one end rather than interleaved.
    if (sort.key === "cycle") return lower(cycleLabel(r) || "");
    return "";
  };
  rows.sort((a, b) => {
    const av = pick(a);
    const bv = pick(b);
    if (av === bv) {
      // Stable tiebreak so equal keys never shuffle between renders.
      return lower(a?.customer_name).localeCompare(lower(b?.customer_name));
    }
    return av > bv ? dir : -dir;
  });
  return rows;
}

/** Has the survey gone out for this completion?
 *
 *  THE DATE IS THE FLAG — null means not sent. There is no boolean beside it,
 *  so the two can never disagree. */
export function isSurveySent(completion) {
  return !!completion?.survey_sent_date;
}

/* ── Review types that TAKE NO SURVEY (operator report 2026-08-11) ────
 *
 * Amber: "no-shows never get surveys" — but the view offered the Survey
 * sent button on them and counted them in "N awaiting survey", so her
 * worklist claimed work that does not exist and her "am I done" number
 * could never reach zero honestly.
 *
 * A SET, NOT A SPECIAL CASE. One list, read by the count, the sort and the
 * cell, so the three can never disagree about which rows need a survey —
 * and adding or removing a type is a one-element edit here rather than
 * three branches to find.
 *
 * MEMBERSHIP:
 *   no-show   THE REPORTED CASE. Nobody attended, so there is no session to
 *             ask anybody about.
 *   skipped   INFERRED, NOT REPORTED — flagged for the operator. A skip is
 *             a review the customer chose not to take, so there is likewise
 *             no meeting to survey; it consumes allowance (that is the
 *             point of recording it) but nothing was delivered to review.
 *             If the operator disagrees, DELETE THE ONE STRING — no other
 *             line changes. Note the small blast radius either way: skips
 *             are hidden from this list unless "Include skips" is ticked
 *             (applySkipVisibility).
 *
 * KEYS VERIFIED against the review-type vocabulary this module already
 * renders (TYPE_LABELS) and the backend's own REVIEW_TYPE_KEYS
 * (shared/review_ops.py) — never typed from a prompt, because a typo here
 * exempts nothing and fails silently. A guard test asserts every entry is a
 * real key.
 *
 *   redo-requested  OPERATOR RULING 2026-08-13, and the entry that breaks
 *             the "no meeting to survey" pattern the two above share — a
 *             meeting DID happen here. It takes no survey because the
 *             meeting failed its purpose and is being rerun: asking the
 *             customer to rate a session everyone has agreed to redo
 *             collects noise and reads as tone-deaf. The survey belongs
 *             to the REDONE review, which will ask for one in the normal
 *             way. Note this type is DELIVERED work in every count
 *             (volume, engineer cards) and exempt only here — the two
 *             questions are unrelated.
 *
 * DELIBERATELY NOT a filter. The rows stay on the list: the never-remove
 * ruling (#537) is about team-performance reading, and a no-show is
 * delivered effort from the engineer's side. It reads as handled, not gone. */
export const NO_SURVEY_REVIEW_TYPES = ["no-show", "skipped", "redo-requested"];

/** Does this completion take a survey at all?
 *
 *  An unrecognised or missing type DOES take one — the safe direction. An
 *  unknown type showing a button Amber can ignore is a five-minute
 *  conversation; an unknown type silently exempted is a customer who never
 *  gets asked. Same reasoning as typeLabel's humanised fallback. */
export function takesSurvey(completion) {
  return !NO_SURVEY_REVIEW_TYPES.includes(completion?.review_type_key);
}

/** Does this row still need Amber to do something?
 *
 *  THE ONE RULE the count, the sort and the cell all read. A row needs
 *  action when it takes a survey and has not had one; everything else is
 *  handled — whether because the survey went out or because none is owed. */
export function needsSurveyAction(completion) {
  return takesSurvey(completion) && !isSurveySent(completion);
}

/** How many rows still need a survey link — the number in the section
 *  heading, so she knows when she is done without scanning the list.
 *
 *  Counted over the rows ACTUALLY SHOWN, so it agrees with what is on screen
 *  under a reviewer filter or a search. One quantity, one number (#532).
 *
 *  EXCLUDES the types that take no survey (2026-08-11). This number is the
 *  answer to "am I done", and a no-show in it made the answer unreachable:
 *  the count could never fall to zero however much work she did. */
export function awaitingSurveyCount(completions) {
  return (completions || []).filter(r => r && needsSurveyAction(r)).length;
}

/**
 * NEEDS-ACTION rows above HANDLED rows, WITH THE EXISTING ORDER PRESERVED
 * INSIDE EACH GROUP (operator ruling 2026-08-11).
 *
 * The split is needsSurveyAction, not isSurveySent (widened 2026-08-11):
 * ONE RULE — needs-action on top, everything handled beneath — so a no-show
 * sits with the sent rows rather than heading a list of work that does not
 * exist. The two groups used to be "unsent" and "sent", which put a row
 * nobody will ever survey at the very top of the queue.
 *
 * ROWS ARE NEVER REMOVED. James reads this list for team performance, so a
 * handled row is notated and sinks — it is not filtered out, and this is a
 * sort, never a filter.
 *
 * An explicit two-bucket partition rather than a comparator on a 0/1 key:
 * Array.prototype.sort is specified stable in modern engines, but a partition
 * PROVES the within-group order is untouched instead of relying on that, and
 * the within-group order is the part a characterization test pinned before
 * this function existed. Apply it AFTER sortCompletions, never instead of it.
 */
export function surveySentFirst(completions) {
  const rows = completions || [];
  const action = rows.filter(r => r && needsSurveyAction(r));
  const handled = rows.filter(r => r && !needsSurveyAction(r));
  return [...action, ...handled];
}

/**
 * Apply a survey-write response to the completions list — the
 * applyStatusWrite idiom, and for the same reason: the row updates from what
 * the SERVER wrote, never from a local guess about what the click meant.
 *
 * The response carries survey_sent_date, so `?? null` is a real clear rather
 * than a fallback. A response that somehow omitted it would clear, which is
 * the safe direction: an unsent row is visible and gets a second click, a
 * wrongly-sent row is silently skipped.
 */
export function applySurveyWrite(completions, result) {
  if (!result?.completion_id) return completions || [];
  return (completions || []).map((row) => (
    row && row.id === result.completion_id
      ? { ...row, survey_sent_date: result.survey_sent_date ?? null }
      : row
  ));
}

/**
 * The engineer cards: [{name, delivered, noShows}], busiest first (the
 * order the backend already returns).
 *
 * The backend retains the full reviewer roster, but this display only shows
 * reviewers who delivered at least one review in the selected range. The
 * cards therefore answer who completed work in this window without filling
 * the row with inactive zeroes.
 *
 * Delivered and no-shows stay separate figures — a no-show is delivered
 * effort from the engineer's side and a service gap from the customer's,
 * and one merged number hides both.
 */
export function engineerCards(byEngineer) {
  return (byEngineer || [])
    .map(g => ({
      id: g?.reviewer_id || null,
      name: g?.reviewer_name || UNKNOWN_REVIEWER,
      delivered: g?.delivered || 0,
      noShows: g?.no_shows || 0,
    }))
    .filter(card => card.delivered > 0);
}

/* ── The type breakdown ──────────────────────────────────────────────
   There is NO local list of review types here, deliberately. There used
   to be (TYPE_ORDER), and it silently fell out of step: #486 added
   "skipped" to the backend's REVIEW_TYPE_KEYS and never here, so a
   recorded skip was counted server-side, correctly excluded from
   `delivered`, and then VANISHED from the breakdown — a live view read
   "15 of 16" above a table summing to 15, with nothing saying why.

   The fix is a mechanism, not a second hand-maintained list. by_type
   arrives from the server ALREADY ZERO-FILLED across every known type
   (review_ops.count_by_type starts from empty_type_counts(), built from
   REVIEW_TYPE_KEYS) — so the payload IS the authoritative key set, and
   iterating it means there is no local list left to drift. Order comes
   free: empty_type_counts() builds in REVIEW_TYPE_KEYS order, and both
   json.dumps and JSON.parse preserve insertion order.

   The zero-fill guarantee now lives in exactly one place, the server,
   and is pinned there by a test on count_by_type([]). */

/* ── "Scheduled" READS "Completed" (operator ruling 2026-08-12) ────────
 *
 * Amber, on the team call: "I was confused... I thought these were all just
 * still scheduled on the calendar." The Dataverse label for a normal
 * delivered review is "Scheduled", and on a list of FINISHED work that
 * reads as future tense — the rows are reviews that happened.
 *
 * DISPLAY ONLY, the contractDisplayLabel pattern. The wire value
 * ("scheduled"), the Dataverse option label ("Scheduled") and every stored
 * record are untouched (CONTENT-01: legacy literals are quoted exactly);
 * the rename happens at the point of render and nowhere else.
 *
 * ONE STRING, TWO LOOKUP SHAPES, because the payload gives two:
 *   completions carry review_type_key ("scheduled") -> typeLabel
 *   last_review carries only the LABEL ("Scheduled") -> reviewTypeDisplayLabel
 * Both resolve through this constant so they cannot drift apart, and a guard
 * test asserts the two paths agree.
 *
 * WHERE IT IS DELIBERATELY *NOT* APPLIED: the Review Complete dialog's
 * picker (REVIEW_OUTCOMES in SecureShield365.js). There the engineer is
 * choosing what KIND of review this was, before anything is recorded —
 * "Completed review" is not a kind, and it would read as a state the click
 * is about to create. Flagged for the operator rather than silently skipped.
 *
 * THE OPERATOR'S ANSWER, 2026-08-13: the picker gets its own word,
 * "Standard review" — so the one wire key "scheduled" now has THREE display
 * contexts, and each is a separate question:
 *
 *   picker        "Standard review"  what KIND of review is this?
 *   delivery list "Completed"        what happened? (finished work)
 *   wire / CRM    "Scheduled"        the stored vocabulary, never renamed
 *
 * Both display strings live here, as one-string constants, for the reason
 * the two lookup shapes already share one: three literals scattered across
 * two files drift, and the drift is silent because each reads plausibly on
 * its own screen. The guard test asserts all three contexts at once, so a
 * rename of any one of them fails loudly rather than quietly splitting the
 * vocabulary.
 */
export const REVIEW_TYPE_COMPLETED_LABEL = "Completed";

/** The PICKER's word for the same wire key — "what kind of review was
 *  this", asked before anything is recorded. Consumed by REVIEW_OUTCOMES
 *  in SecureShield365.js; never by a list of finished work. */
export const REVIEW_TYPE_STANDARD_LABEL = "Standard review";

/** The display name for wire key "redo-requested" (Dataverse 125470005).
 *
 *  RENAMED FROM "Redo requested" (Amber, 2026-08-13 call): she rejected
 *  both "Redo" and "Re-review" as words for what happens next. Display
 *  only — CONTENT-01. The wire key stays "redo-requested", the Dataverse
 *  integer stays 125470005, and the CRM option label stays "Redo
 *  Requested" because portal work does not rename Dataverse metadata.
 *
 *  THAT LAST POINT IS WHY THIS CONSTANT IS ALSO A DATAVERSE-LABEL MAPPING
 *  (see REVIEW_TYPE_DISPLAY_LABELS). Two independent sources feed the
 *  displayed type: the wire key through TYPE_LABELS, and the raw CRM
 *  label through reviewTypeDisplayLabel on payloads that carry the label
 *  and not the key. Renaming only the first would leave "Redo Requested"
 *  on lastReviewLabel and the dialog's context line. */
export const REVIEW_TYPE_RESCHEDULE_LABEL = "Reschedule requested";

/** The heading above the picker. "Outcome" read as a result — the thing
 *  the list columns report — when the question is which kind of review to
 *  record (operator ruling 2026-08-13). */
export const REVIEW_TYPE_PICKER_HEADING = "Review Type";

/** The Dataverse label -> what a reader sees. One entry, same shape as
 *  CONTRACT_DISPLAY_LABELS. An absent/empty label passes through falsy so
 *  callers keep their own "—" fallback. */
// Keyed on the DATAVERSE option labels, quoted exactly as CRM stores them
// (CONTENT-01: legacy literals are quoted, never rewritten at the source).
// "Redo Requested" is option 125470005's label [V] metadata capture
// docs/reconstruction/metadata/pc_reviewcompletion.typed-attrs.json — it is
// NOT renamed in Dataverse, so it is renamed here, on the way to the screen.
// "Re-review" is deliberately absent: historical rows keep saying Re-review.
const REVIEW_TYPE_DISPLAY_LABELS = {
  Scheduled: REVIEW_TYPE_COMPLETED_LABEL,
  "Redo Requested": REVIEW_TYPE_RESCHEDULE_LABEL,
};

export function reviewTypeDisplayLabel(label) {
  if (!label) return label;
  return REVIEW_TYPE_DISPLAY_LABELS[label] || label;
}

// Display copy only. A key with no entry falls back to a humanised form
// of itself rather than rendering undefined: a sixth review type showing
// up with an ugly label is a five-minute fix, one that VANISHES is a bug
// nobody files.
export const TYPE_LABELS = {
  scheduled: REVIEW_TYPE_COMPLETED_LABEL,
  // FORWARD-ONLY RENAME. "Re-review" leaves the picker but KEEPS its label
  // here: rows recorded as re-reviews before 2026-08-13 are historical fact,
  // and relabelling them would rewrite what those reviews were called when
  // they happened. Delivery and history surfaces still say "Re-review".
  "re-review": "Re-review",
  "off-cycle": "Off-cycle",
  "redo-requested": REVIEW_TYPE_RESCHEDULE_LABEL,
  "no-show": "No-show",
  skipped: "Skipped",
  unknown: "Unrecognised type",
};

export function typeLabel(key) {
  if (TYPE_LABELS[key]) return TYPE_LABELS[key];
  const text = String(key ?? "").replace(/[-_]+/g, " ").trim();
  if (!text) return "Unrecognised type";
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** The type to render for a COMPLETION row, from whichever field it has.
 *  Prefers the key (the payload's own vocabulary, and what typeLabel is
 *  built for) and falls back to mapping the label — so a row from an older
 *  payload shape still renames correctly. Null/absent yields null, and the
 *  cell keeps its "—". */
export function completionTypeLabel(completion) {
  const key = completion?.review_type_key;
  if (key) return typeLabel(key);
  return reviewTypeDisplayLabel(completion?.review_type) || null;
}

/**
 * [{key, label, count}] over EVERY type the payload carries, in the
 * server's order, with `unknown` forced last.
 *
 * Scoped to the whole selected RANGE, never to the current filter — the
 * counts sum to volume.total_rows and do not move when the Include-skips
 * checkbox or the reviewer filter changes. A breakdown that shifts when
 * you tick a checkbox is one nobody can trust.
 */
export function typeBreakdown(byType) {
  const source = byType || {};
  const keys = Object.keys(source).filter(k => k !== "unknown");
  const rows = keys.map(key => ({
    key, label: typeLabel(key), count: source[key] || 0,
  }));
  if (source.unknown) {
    rows.push({ key: "unknown", label: typeLabel("unknown"),
                count: source.unknown });
  }
  return rows;
}

/* ── Phase A.5: the status write ─────────────────────────────────────
   She sets state, the system stamps dates. The form asks for a date ONLY
   when the chosen status needs one, and it learns which statuses those
   are from meta.status_options — the same STATUS_DATE_RULE table the
   backend validates against. The form and the write therefore cannot
   disagree about which statuses move a date. */

// Mirrors shared/review_ops.STATUS_DATE_RULE's rule kinds.
export const DEFAULT_TODAY = "default_today";
export const REQUIRE_DATE = "require_date";
export const NO_DATE_CHANGE = "no_date_change";
// The BLOCKING rule (2026-08-11). Requires a date exactly like REQUIRE_DATE
// but the date lands in pc_blockeduntildate, NOT pc_nextauditdate — so the
// confirm must not claim the next audit date moves, because it does not.
export const REQUIRE_BLOCKING_DATE = "require_blocking_date";

/**
 * EVERY date_rule string the backend can emit in meta.status_options.
 *
 * A MIRROR OF THE LIST, not of the file: backend/shared/review_ops.py owns
 * DEFAULT_TODAY / REQUIRE_DATE / NO_DATE_CHANGE / REQUIRE_BLOCKING_DATE, and
 * this array exists so a rule string with no frontend behaviour fails a test
 * instead of silently rendering no date input.
 *
 * That is not hypothetical. Access Meeting shipped with
 * "require_blocking_date" against a frontend that knew three strings; the
 * status became selectable, no input rendered, and every write was refused.
 * The guard in reviewOps.statusDateRules.test.js is what makes the next one
 * a red test rather than a production bug.
 */
export const KNOWN_DATE_RULES = [
  DEFAULT_TODAY, REQUIRE_DATE, NO_DATE_CHANGE, REQUIRE_BLOCKING_DATE,
];

/** Writable statuses for the dropdown, in the order they appear in the
 *  workflow: the two that book, then the blockers. */
export function statusOptions(meta) {
  return (meta?.status_options || []).filter(o => o && o.value != null);
}

export function statusDateRule(meta, statusValue) {
  const match = statusOptions(meta)
    .find(o => String(o.value) === String(statusValue));
  return match ? match.date_rule : null;
}

/**
 * True when the status cannot be recorded WITHOUT a date — the inline
 * editor gates on this, so Meeting Scheduled and Prebook still stop for
 * a date before the confirm appears.
 */
export function statusNeedsDate(meta, statusValue) {
  const rule = statusDateRule(meta, statusValue);
  // REQUIRE_BLOCKING_DATE gates identically — it is required with the same
  // strictness, and the backend refuses a dateless write either way. Only
  // the COLUMN the date lands in differs.
  return rule === REQUIRE_DATE || rule === REQUIRE_BLOCKING_DATE;
}

/**
 * True when the status TAKES a date at all — required or optional.
 *
 * Deliberately distinct from statusNeedsDate: Booking Sent offers a date
 * field but does not gate on it, so a recorder who just clicks through
 * gets exactly today's behaviour in the same number of clicks. The
 * common case must not get slower to serve the occasional backdate.
 */
export function statusTakesDate(meta, statusValue) {
  const rule = statusDateRule(meta, statusValue);
  return rule === REQUIRE_DATE || rule === DEFAULT_TODAY
    || rule === REQUIRE_BLOCKING_DATE;
}

/**
 * What the pending change will do, for the confirm. Returns null when
 * the change needs no confirm at all.
 *
 * A confirm appears whenever a DATE MOVES, and never when one does not:
 * the four no-date statuses write immediately, because a confirm on a
 * harmless change trains the reader to click through the two that matter.
 *
 * `lostDate` is the appointment about to be destroyed — Booking Sent
 * stamping today over a booked meeting. Legitimate (the customer
 * cancelled and a fresh link is going out) so it is warned, never
 * blocked, but the copy names the specific date because it exists
 * nowhere else once overwritten.
 */
export function movesNextAuditDate(rule) {
  return rule === REQUIRE_DATE || rule === DEFAULT_TODAY;
}

export function statusChangePreview({
  meta, row, statusValue, suppliedDate, today,
}) {
  const rule = statusDateRule(meta, statusValue);
  if (!rule || rule === NO_DATE_CHANGE) return null;

  const label = (statusOptions(meta)
    .find(o => String(o.value) === String(statusValue)) || {}).label || "";

  // ── THE BLOCKING RULE GETS ITS OWN PREVIEW ──────────────────────────
  // Every consequence line below is about pc_nextauditdate: the date being
  // replaced, the date lost, the backdate, the grace window, whether the row
  // enters Backlog. NONE of them apply to a blocking date, and reusing them
  // would have the confirm announce "this sets the next audit date to Aug 19"
  // for a write that leaves that column untouched — the same class of bug as
  // the missing input, one level down.
  //
  // So this branch states what IS happening (the access meeting date is
  // recorded) and what is NOT (the audit is still due when it was due), and
  // carries none of the next-audit machinery.
  if (rule === REQUIRE_BLOCKING_DATE) {
    if (!suppliedDate) return null;   // nothing to preview until picked
    return {
      rule,
      label,
      customerName: row?.customer_name || row?.slug || "",
      movesNextAuditDate: false,
      // The blocking date's OWN current value — never next_audit_date.
      currentDate: row?.blocked_until_date || null,
      newDate: suppliedDate,
      // The audit due date, stated so the confirm can say it is unchanged.
      nextAuditDate: row?.next_audit_date || null,
      daysOverdue: row?.days_overdue ?? null,
      lostDate: null,
      statesFollowUpWindow: false,
      isBackdated: false,
      daysBack: 0,
      entersBacklog: false,
      graceDays: meta?.backlog_grace_days ?? 10,
      usesWorkingGrace: false,
      workingGraceDays: meta?.backlog_grace_working_days ?? 10,
    };
  }

  const currentDate = row?.next_audit_date || null;
  // DEFAULT_TODAY falls back to today when nothing is picked;
  // REQUIRE_DATE has nothing to preview until one is.
  const newDate = suppliedDate
    || (rule === DEFAULT_TODAY ? today : null);
  if (!newDate) return null;

  // A future date being replaced by an earlier one is a real loss.
  const lostDate = (currentDate && today
    && currentDate > today && newDate < currentDate) ? currentDate : null;

  // THE CONSEQUENCE OF A BACKDATE. BACKLOG_GRACE_DAYS is measured from
  // the column being written, so backdating Booking Sent past the grace
  // window puts the customer into Backlog on save. Correct — the link
  // went out and they have not booked — but it must be said before the
  // write, not discovered when the row moves. Derived from the SAME
  // threshold the backlog rule uses (meta.backlog_grace_days); never a
  // second copy of the number.
  const graceDays = meta?.backlog_grace_days ?? 10;
  const isBackdated = rule === DEFAULT_TODAY && !!today && newDate < today;
  const daysBack = (today && newDate < today)
    ? Math.round((new Date(`${today}T00:00:00`) - new Date(`${newDate}T00:00:00`))
        / 86400000)
    : 0;
  // WHICH grace applies (2026-08-10). The three follow-up statuses moved
  // to a 10-WORKING-day window, so the calendar prediction below would
  // be wrong for exactly the status this preview fires on most (Booking
  // Sent): it would announce Backlog for a backdate that now lands in
  // Just past due. We deliberately do NOT predict the bucket for those
  // statuses — counting working days here would be a second
  // implementation of the bucket rule, the drift this module refuses
  // elsewhere. Instead the copy STATES the window, and the server's
  // reclassified row (applyStatusWrite) shows the real bucket the moment
  // the write returns — one implementation, truth on a two-second delay.
  const followUpStatuses = meta?.follow_up_grace_statuses || [];
  const usesWorkingGrace = followUpStatuses.includes(statusValue);
  const workingGraceDays = meta?.backlog_grace_working_days ?? 10;
  const entersBacklog = !usesWorkingGrace && daysBack > graceDays;

  return {
    rule,
    label,
    customerName: row?.customer_name || row?.slug || "",
    // True for these two rules and false for the blocking one, so a consumer
    // never has to test the rule string to know what the copy may claim.
    movesNextAuditDate: movesNextAuditDate(rule),
    currentDate,
    newDate,
    lostDate,
    // Booking Sent always states where the follow-up window runs from —
    // on the routine press too, not only the backdated one. Learning the
    // rule on the ordinary press is what stops it surprising anyone.
    statesFollowUpWindow: rule === DEFAULT_TODAY,
    isBackdated,
    daysBack,
    entersBacklog,
    graceDays,
    usesWorkingGrace,
    workingGraceDays,
  };
}

/**
 * The follow-up-window sentence for the Booking Sent confirm, or null
 * when the status does not state one.
 *
 * Shown on EVERY Booking Sent press, not only backdated ones — learning
 * the rule on the routine press is what stops it surprising anyone
 * later. But the CONTRAST is conditional: on the routine press the date
 * IS today, so "runs from Aug 8, not from today" contradicts itself and
 * the line above it already says "(today)".
 *
 * The concrete date stays named on the backdated branch. "the date
 * above" or "the date selected" would be vaguer and make the reader
 * look up to find it, which is the opposite of what the sentence is for.
 *
 * graceDays comes from the preview (meta.backlog_grace_days), never a
 * hardcoded 10.
 */
export function followUpWindowCopy(preview, fmtDate) {
  if (!preview?.statesFollowUpWindow) return null;
  // Follow-up statuses state the WORKING-day window (Mon-Thu), which is
  // the cadence the team actually works; everything else keeps the flat
  // calendar sentence.
  const working = preview.usesWorkingGrace;
  const days = working ? preview.workingGraceDays : preview.graceDays;
  const unit = working ? "working-day (Mon–Thu)" : "day";
  if (!preview.isBackdated) {
    return `The ${days}-${unit} follow-up window runs from today.`;
  }
  const when = fmtDate ? fmtDate(preview.newDate) : preview.newDate;
  return `The ${days}-${unit} follow-up window runs from ${when}, not from today.`;
}

/**
 * Apply a status-write response to the customer list.
 *
 * The row is patched IN PLACE and marked `just_moved` when the write
 * changed which bucket it belongs to — it is NOT removed. A row
 * disappearing the instant you click it is indistinguishable from a
 * failure, and setting Booking Sent on a backlog customer moves them to
 * Approaching, so the common case would be the confusing one. The note
 * clears on the next fetch.
 */
export function applyStatusWrite(customers, result) {
  if (!result?.slug) return customers || [];
  return (customers || []).map((row) => {
    if (!row || row.slug !== result.slug) return row;
    const movedTo = row.bucket !== result.bucket ? result.bucket : null;
    return {
      ...row,
      workflow_status: result.workflow_status,
      workflow_status_value: result.workflow_status_value,
      next_audit_date: result.next_audit_date,
      days_overdue: result.days_overdue,
      days_until: result.days_until,
      working_days_overdue: result.working_days_overdue,
      grace_basis: result.grace_basis,
      severity: result.severity,
      // THE BLOCKING FIELDS MUST BE TAKEN FROM THE RESPONSE, NOT PRESERVED
      // (2026-08-11). The spread above keeps every field the response does
      // not mention, and these three were not in the response when the
      // feature shipped — so a status change left the OLD flags in place and
      // the "Waiting on a customer meeting" section held the row whatever the
      // database said, until a full refetch. `?? null` rather than `||` so a
      // deliberate null clears, and so a response from an OLDER backend (no
      // blocking fields) also clears rather than silently keeping stale ones:
      // clearing is the safe direction, because a row wrongly in the
      // actionable list is visible and a row wrongly hidden is not.
      blocked_until_date: result.blocked_until_date ?? null,
      blocking_suppressed: result.blocking_suppressed ?? false,
      blocking_lifted: result.blocking_lifted ?? false,
      // THE AGE, FROM THE RESPONSE, for exactly the reason above: the
      // status just changed, so its age is zero, and a preserved value
      // would leave the chip advertising the PREVIOUS status's age. `??
      // null` so an older backend (no age field) blanks the age rather
      // than keeping a stale number — a missing age reads as "we don't
      // know", which is true, and a wrong one does not.
      workflow_status_age_days: result.workflow_status_age_days ?? null,
      // Keep the ORIGINAL bucket so the row stays where she is looking;
      // just_moved carries where it now belongs.
      just_moved: movedTo,
    };
  });
}

// Display names for the buckets. `grace` is an INTERNAL id and the word
// never reaches a user: "grace" reads as leniency Patriot granted,
// when it is simply a date that slipped.
export const BUCKET_LABELS = {
  backlog: "Backlog",
  approaching: "Approaching",
  grace: JUST_PAST_DUE_LABEL,
  future: "Scheduled",
  unscheduled: "Unscheduled",
};

/* ── Customer-requested skip ─────────────────────────────────────────
   A skip spends a review the customer paid for, and unlike a delivered
   review there is no meeting anyone remembers to contradict it. The
   dialog therefore states the COST and the caution, and captures who
   asked. */

export const SKIP_REQUESTED_BY_MAX = 200;   // [V] pc_skiprequestedby
export const SKIP_OTHER = "__other__";

/** Active portal contacts, for the "who requested it" dropdown.
 *  Active is status_value 125470001 — a Pending or Removed contact is
 *  not somebody who can have emailed asking for a skip. */
export function skipRequesterOptions(accessList) {
  return (accessList || [])
    .filter(m => m && m.status_value === 125470001 && (m.email || m.name))
    .map(m => ({
      id: m.id || m.email,
      label: m.name && m.email ? `${m.name} (${m.email})`
        : (m.name || m.email),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** The string written to pc_skiprequestedby, or "" when incomplete. */
export function skipRequesterValue({ selection, otherText, options }) {
  if (!selection) return "";
  if (selection === SKIP_OTHER) return (otherText || "").trim();
  const match = (options || []).find(o => o.id === selection);
  return match ? match.label : "";
}

/** Submit gating: a requester is REQUIRED, and the cap is enforced here
 *  rather than by truncation — a silently shortened name loses it. */
export function skipSubmitBlocked({ selection, otherText, options, context }) {
  if (context && context.scheduled_locked) return "year_full";
  const value = skipRequesterValue({ selection, otherText, options });
  if (!value) return "no_requester";
  if (value.length > SKIP_REQUESTED_BY_MAX) return "requester_too_long";
  return null;
}

/**
 * Which cost sentence the confirm shows.
 *
 * It branches on the SAME on-cycle signal the WRITE branches on —
 * context.cycle, straight from the server, never recomputed here. On a
 * null-cycle customer the skip consumes nothing and moves no position,
 * so the "spends one of their N reviews" sentence would be FALSE; on 42
 * such customers that is worse than saying nothing. The caution is in
 * both branches — it is about the customer relationship, not the
 * accounting.
 */
export function skipCostCopy(context) {
  if (!context) return null;
  const onCycle = !!context.cycle;
  if (!onCycle) {
    return {
      onCycle: false,
      cost: "This customer is not on a tracked review cycle, so recording "
        + "the skip consumes no review and moves no cycle position. It is "
        + "recorded as history only.",
    };
  }
  const perYear = context.allowance?.per_year;
  const year = context.cycle.contract_year;
  // REMAINING, not "used X of Y" — the recorder is asking "does this
  // customer still have reviews left?", and the remaining count answers
  // it directly. Computed server-side (review_cycle.allowance_position)
  // from pc_reviewnumber, the allowance authority, with both clamps
  // applied there — so this renders a number, it does not derive one.
  const remaining = context.reviews_remaining_after;
  const base = `This spends one of their ${perYear} reviews for contract `
    + `year ${year}`;
  if (typeof remaining !== "number") {
    // Older backend that predates the field — state the cost without
    // inventing a remaining count.
    return { onCycle: true, cost: `${base}.` };
  }
  // Zero in plain words. "0 will remain" reads like a calculation; a
  // person would say the year is spent.
  const tail = remaining === 0
    ? " — it is their last one for the year."
    : remaining === 1
      ? " — 1 will remain."
      : ` — ${remaining} will remain.`;
  return { onCycle: true, cost: `${base}${tail}` };
}

// Shared with the Review Complete dialog's lock: a skip consumes
// allowance identically, so a full year blocks it for the same reason.
// The dialog's trailing "record this as an off-cycle review" is NOT
// reused — there is no outcome picker here, so that instruction would
// point at a control that does not exist. (That line named Re-review too
// until 2026-08-13, when Re-review left the picker.)
export function yearFullReason(context, fmtDate) {
  const perYear = context?.allowance?.per_year;
  const end = context?.current_window_end;
  return `This customer has used all ${perYear} reviews for the contract `
    + `year, which runs until ${fmtDate ? fmtDate(end) : end}.`;
}

export const SKIP_CAUTION =
  "Only record this at the customer's request. A skip costs the customer "
  + "a review they paid for, and unlike a delivered review there is no "
  + "meeting anyone remembers to contradict it.";

/** The Recorded list, with skips excluded unless asked for. The engineer
 *  cards do NOT use this — their exclusion is server-side and absolute. */
export function applySkipVisibility(completions, includeSkips) {
  if (includeSkips) return completions || [];
  return (completions || []).filter(c => c?.review_type_key !== "skipped");
}

// ── Booking readiness (SPEC-booking-readiness; display-only PR) ──────
// The server ships state KEYS and classifies against the caller's
// today; labels, tones and tooltips live here so they are testable.
// readiness === null means the blob index was unavailable that refresh
// — rendered "—", never a guessed state.

export const READINESS_META = {
  ready:               { label: "Ready to book",   tone: "green" },
  // Legacy allowance only, server-classified: a fresh scan exists, but the next
  // review is still outside the scheduling lead window. Not Ready-only, because
  // this is no longer the ordinary green cadence prompt; muted because
  // booking is exceptional mid-cycle work, not the default next action.
  fresh_scan:          { label: "Fresh scan",      tone: "muted" },
  in_review:           { label: "In review",       tone: "blue" },
  waiting_on_scan:     { label: "Waiting on a scan", tone: "muted" },
  no_usable_data:      { label: "No usable data",  tone: "red" },
  // Staleness tier 3 (spec open question 4): past the rescan threshold
  // the row is NOT ready — its own state, not a modifier on ready, so
  // the ready-only filter and the label stay unambiguous. Thresholds
  // live in the BACKEND (booking_readiness constants) and arrive via
  // meta.ready_fresh_days / meta.ready_aging_days /
  // meta.ready_rescan_days — never hardcode any threshold here.
  rescan_needed:       { label: "Rescan needed",   tone: "orange" },
  // First precedence, server-side: nothing downstream matters without
  // a contract, so the pill must never instruct a booking, rescan, or
  // approval for an expired one. The action is a CRM correction.
  contract_expired:    { label: "Contract expired", tone: "red" },
  // SECOND precedence, server-side (2026-08-17). Every review this
  // contract year owes has been delivered and the recorded term holds no
  // next review year, so there is nothing to book until a renewal is
  // recorded.
  //
  // MUTED, AND THE TONE IS REUSED RATHER THAN CHOSEN (operator ruling
  // R3). Colour on this column means "there is something to do here";
  // this state's whole content is that there is not. It wears the same
  // no-colour treatment a finished One Time already wears on Customer
  // Management ("One Time · Done", renewalPill below) — the shipped
  // token, not a new hue. #552's precedent stands: a new colour is an
  // explicit operator exception and this is not one.
  //
  // THE RESERVATION, made testable: grey means "no action" and nothing
  // else on the row may borrow it. Grey the ACTIONS, not the facts — the
  // scan-method and workflow pills keep their own tones, because the
  // customer is still live.
  all_reviews_complete: { label: "All Reviews Complete", tone: "muted" },
};

/** The two pill keys that are NOT a READINESS_META state. Declared beside
 *  the states so the whole key vocabulary is readable in one place; the
 *  reasons each exists are at readinessKey and readinessPillForRow. */
export const READINESS_CHIP_UNKNOWN = "unknown";
export const READINESS_CHIP_READY_AGING = "ready_aging";
export const READINESS_CHIP_REBOOK = "rebook";

export const READINESS_REASON_LABELS = {
  never_scanned: "never scanned",
  quarantined: "scan quarantined",
  never_published: "scored, never published",
  // ── all_reviews_complete's THREE SUB-LINES (operator ruling R1,
  //    2026-08-17). PINNED COPY — these words are the operator's, not a
  //    paraphrase, and a copy edit here is a copy edit to a ruling.
  //
  // One state, one wire key, one chip — three second lines, because
  // "what now" genuinely differs and one shared line was wrong for three
  // rows in four (census 2026-08-11: 37 case (i), 13 case (ii)).
  //
  //   engagement_delivered  a One Time engagement, delivered. NOT
  //       "awaiting renewal": a one-time engagement does not renew, and
  //       that exact wording is what the 2026-08-11 ruling removed from
  //       the Customer Management tag as false work.
  //   term_exhausted        the term itself is ending — the natural
  //       interval would have left it. "Awaiting renewal" is TRUE here.
  //   allowance_exhausted   the year's allowance is spent but the term
  //       is still running. 5.11 Tactical has 202 days left; a renewal
  //       line here sends the delivery team chasing nothing.
  //
  // The keys match the backend's warning kinds (review_cycle
  // TERM_EXHAUSTED / ALLOWANCE_EXHAUSTED) so the pill and the Review
  // Complete dialog's warning describe one situation with one vocabulary.
  engagement_delivered: "engagement delivered",
  term_exhausted: "awaiting renewal",
  allowance_exhausted: "allowance used for this contract year",
};

/** Pill text. "In review" carries its age ("In review — 3 d") because
 *  the age IS Amber's question on that state; "No usable data" carries
 *  its reason because without it the row cannot be actioned. */
export function readinessLabel(readiness) {
  if (!readiness || !readiness.state) return "—";
  const meta = READINESS_META[readiness.state];
  if (!meta) return "—";
  if (readiness.state === "in_review") {
    const d = readiness.days_in_review;
    return Number.isInteger(d) ? `${meta.label} — ${d} d` : meta.label;
  }
  // TWO states carry a reason, and both ride THIS mechanism rather than
  // a second one: "No usable data" cannot be actioned without its
  // reason, and "All Reviews Complete" answers three different "what
  // now"s (READINESS_REASON_LABELS). An unmapped reason degrades to the
  // bare label — a frontend older than its backend says less, never
  // something wrong.
  if (readiness.state === "no_usable_data"
      || readiness.state === "all_reviews_complete") {
    const reason = READINESS_REASON_LABELS[readiness.reason];
    return reason ? `${meta.label} — ${reason}` : meta.label;
  }
  return meta.label;
}

/** Tone key for the pill; the component maps tones to theme colors.
 *  Ready splits by the server-computed staleness field — green while
 *  fresh, amber once aging (45–90 days by default; the server owns the
 *  numbers) — the frontend never recomputes days. */
export function readinessTone(readiness) {
  const meta = readiness && READINESS_META[readiness.state];
  if (!meta) return "muted";
  if (readiness.state === "ready" && readiness.staleness === "aging") {
    return "yellow";
  }
  return meta.tone;
}

/* ── The booking-workflow override on the Readiness pill (2026-08-11) ──
 *
 * Operator report (Amber): "it's very confusing when you have all of these
 * meetings scheduled, but then the readiness still says ready to book." The
 * pill answered "what does the scan say"; the reader was asking "is there
 * anything for me to do on this row". Once a booking is sent or a meeting is
 * on the calendar, the answer is no, whatever the scan says.
 *
 * A DATA MAP, NOT AN IF-CHAIN, because this list is expected to grow: Access
 * Meeting (125470011) is the next candidate and should cost one line here.
 *
 * ZERO NEW COLOUR VALUES. "blue" and "muted" are existing tone keys — the
 * component already maps both to theme colours for in_review and
 * waiting_on_scan respectively.
 *
 * THE TWO COLLISIONS, DECIDED NOT OVERLOOKED. "blue" already means in_review
 * and "muted" already means waiting_on_scan. Both are accepted, because the
 * LABEL differs in every case ("Booking sent" vs "In review — 3 d", "Booked"
 * vs "Waiting on a scan") so a reader is never left unable to tell which
 * state they are looking at — and because grey carries one coherent idea
 * across both of its meanings: nothing here is actionable right now. Do not
 * introduce a new hue to separate them; a hue nobody has calibrated on this
 * background is a worse problem than a shared one with distinct words. */
const WORKFLOW_MEETING_SCHEDULED = 125470005;

/* `key` (2026-08-13) is the pill's STABLE IDENTITY, added for the filter
 * chips: the label is display text and the tone is shared, so neither can
 * identify a pill on its own, and a persisted filter selection must survive
 * a copy edit. Two statuses SHARE the "booked" key on purpose — they render
 * one pill, so they are one chip; the sort already treats them as one rank
 * for the same reason. */
export const BOOKING_PILL_OVERRIDES = {
  125470004: { tone: "blue",  label: "Booking sent",  key: "booking_sent" },   // Booking Sent
  [WORKFLOW_MEETING_SCHEDULED]: {
    tone: "muted", label: "Booked", key: "booked",
  }, // Meeting Scheduled
  125470006: { tone: "muted", label: "Booked",        key: "booked" },         // Prebook - No Reschedule
  // Access Meeting (2026-08-11), the first BLOCKING status. NOT "Booked" —
  // nothing is booked, and the label must not claim more than the fact. A
  // prerequisite conversation is scheduled; the audit is not.
  125470011: { tone: "muted", label: "Access meeting", key: "access_meeting" },
};

export const BOOKING_SOURCE_CUSTOMER_REQUEST = "customer_request";
export const CUSTOMER_BOOKED_LABEL = "Customer booked";

/* The shared `booked` key and muted tone deliberately keep customer-created
 * Meetings Scheduled in Amber's existing Booked filter and sort rank. James
 * approved the slate-filled presentation on 2026-08-23 because the prior blue
 * Self Booked treatment was too close to Booking sent. Presentation is kept
 * separate so the filter chip continues to describe the shared booked state. */
function isCustomerBookedReview(workflowStatusValue, booking) {
  return workflowStatusValue === WORKFLOW_MEETING_SCHEDULED
    && booking?.source === BOOKING_SOURCE_CUSTOMER_REQUEST;
}

function bookingOverrideLabel(override, workflowStatusValue, booking) {
  if (isCustomerBookedReview(workflowStatusValue, booking)) {
    return CUSTOMER_BOOKED_LABEL;
  }
  return override.label;
}

function bookingOverrideTone(override, workflowStatusValue, booking) {
  if (isCustomerBookedReview(workflowStatusValue, booking)) {
    return "muted";
  }
  return override.tone;
}

/** States the override must NEVER hide.
 *
 *  contract_expired is first-precedence server-side for the reason stated in
 *  READINESS_META: the pill must never instruct a booking on an expired
 *  contract. no_usable_data means there is nothing to review. Both stay red
 *  through any workflow status, because a booking already sent against
 *  either one is the error the pill exists to surface, not a reason to stop
 *  surfacing it.
 *
 *  THE AMBER STATES ARE DELIBERATELY NOT HERE. rescan_needed,
 *  ready-but-aging and rescan_needed DO get overridden: if a booking went out
 *  anyway that was a judgment the operator made, and the row's real scan state
 *  stays available in the tooltip. Decided, not overlooked. */
export const READINESS_UNOVERRIDABLE_STATES = [
  "contract_expired",
  "no_usable_data",
  // all_reviews_complete joins them for the SAME reason, not a new one
  // (2026-08-17): a booking already sent against a row with no reviews
  // left in the contract year is precisely the error this pill exists to
  // surface, so "Booking sent" must not speak over it. Whether the
  // workflow moved is not the question the reader is asking here.
  //
  // It is the third member and the list is still short by design — the
  // AMBER states stay overridable, which is the decision recorded above.
  "all_reviews_complete",
];

/** The pill KEY for a readiness blob — its identity when the workflow has
 *  not spoken. The states are their own keys, with two additions:
 *
 *   * READINESS_CHIP_UNKNOWN for a blob that is absent or names a state this
 *     build does not know. It is a REAL population ("readiness unavailable
 *     this refresh"), never a silent drop;
 *   * READY_AGING, because `ready` renders as TWO pills — readinessTone
 *     splits it green/yellow on the server's staleness field while
 *     readinessLabel says "Ready to book" for both. A key that ignored the
 *     split would put a yellow-pilled row behind a green dot, and "all of
 *     the green that are ready to book" is the entire ask.
 *
 *  The aging branch mirrors readinessTone's, and a guard test asserts every
 *  row sharing a key also shares a tone — so the two cannot drift. */
export function readinessKey(readiness) {
  const meta = readiness && READINESS_META[readiness.state];
  if (!meta) return READINESS_CHIP_UNKNOWN;
  if (readiness.state === "ready" && readiness.staleness === "aging") {
    return READINESS_CHIP_READY_AGING;
  }
  return readiness.state;
}

/**
 * The Readiness pill's tone and label together, given the row's readiness
 * blob and its workflow status value.
 *
 * Returns `{ tone, label, key, overriddenFrom }`. `overriddenFrom` is the
 * label the scan alone would have produced when the workflow spoke for the
 * pill, and null otherwise — the caller uses it to keep the real state in
 * the tooltip, so the override never makes a state unreachable. `key` is the
 * pill's stable identity (2026-08-13), emitted from the same branch that
 * decides the tone and the label so a filter keyed on it can never disagree
 * with what the row is wearing.
 *
 * Delegates to readinessTone / readinessLabel for every non-override case;
 * neither of those changes signature or behaviour, and both keep their own
 * tests.
 */
export function readinessPill(readiness, workflowStatusValue, booking = null) {
  const label = readinessLabel(readiness);
  const tone = readinessTone(readiness);
  const key = readinessKey(readiness);
  if (READINESS_UNOVERRIDABLE_STATES.includes(readiness?.state)) {
    return { tone, label, key, overriddenFrom: null };
  }
  const override = BOOKING_PILL_OVERRIDES[workflowStatusValue];
  if (!override) return { tone, label, key, overriddenFrom: null };
  const customerBooked = isCustomerBookedReview(workflowStatusValue, booking);
  return {
    tone: bookingOverrideTone(override, workflowStatusValue, booking),
    label: bookingOverrideLabel(override, workflowStatusValue, booking),
    key: override.key,
    overriddenFrom: label,
    ...(customerBooked ? { presentation: "filled_slate" } : {}),
  };
}

/**
 * The pill for a ROW — the entry point every caller should use.
 *
 * WHY THIS EXISTS RATHER THAN CALLING readinessPill DIRECTLY. contract_expired
 * has TWO sources, and readinessPill can only see one of them.
 * `readiness.state` is the first (#508 made it first-precedence), but the row
 * also carries its own `contract_expired` flag from the expired-but-entitled
 * sweep, and readinessRank has trusted that flag since 2026-08-10 precisely so
 * a row cannot sort as actionable because its readiness blob happened to be
 * unavailable. A row with the flag set and readiness === null would otherwise
 * pass the state check and get overridden to "Booked" — the override hiding an
 * expired contract, which is the one thing the precedence rules exist to stop.
 *
 * Returns the un-overridden pill in that case. It does not manufacture a
 * "Contract expired" label the readiness blob cannot support: the Contract
 * column already states it, and the rule here is only that the workflow must
 * not speak over it.
 */
export function readinessPillForRow(row) {
  if (row?.contract_expired) {
    return {
      tone: readinessTone(row?.readiness),
      label: readinessLabel(row?.readiness),
      key: readinessKey(row?.readiness),
      overriddenFrom: null,
    };
  }
  // RESCHEDULE (Amber, 2026-08-13): a row awaiting a new booking link read
  // "Ready to book", which is true of the scan and useless to the reader —
  // the thing to do is send the link. Below contract_expired, which still
  // wins: there is nothing to rebook without a contract.
  //
  // overriddenFrom IS DELIBERATELY null, and this is the load-bearing line.
  // isBookingOverridden reads exactly that field, and BOTH filterReadyOnly
  // and readinessRank read isBookingOverridden — so a non-null value here
  // would silently drop these rows out of the Ready-only filter and re-rank
  // them in the readiness sort. This rename changes NO behaviour, so the
  // pill's text and tone move and nothing else does.
  if (row?.rebook_needed) {
    return {
      tone: "pink", label: REBOOK_PILL_LABEL,
      key: READINESS_CHIP_REBOOK, overriddenFrom: null,
    };
  }
  return readinessPill(row?.readiness, row?.workflow_status_value, row?.booking);
}

/** Did the booking workflow speak for this row's pill?
 *
 *  THE SINGLE PREDICATE for "this row is already booked", used by the pill,
 *  the ready-only filter AND the readiness sort. Driven off
 *  BOOKING_PILL_OVERRIDES through readinessPillForRow, never off a second
 *  list of status integers — two lists of the same thing disagree, and the
 *  next status added to the map has to reach all three consumers at once. */
export function isBookingOverridden(row) {
  return readinessPillForRow(row).overriddenFrom !== null;
}

/** The scan date, shown on EVERY ready row and on rescan rows — if the
 *  date appeared only when something is wrong, nobody could calibrate
 *  and the first amber row would have no reference point. Null for
 *  states where the landing date isn't the point (in_review shows its
 *  age instead). */
export function readinessScanDate(readiness, fmtDate) {
  if (!readiness || !readiness.landing_date) return null;
  if (readiness.state !== "ready" && readiness.state !== "fresh_scan"
      && readiness.state !== "rescan_needed") {
    return null;
  }
  const when = fmtDate ? fmtDate(readiness.landing_date) : readiness.landing_date;
  return readiness.scan_source === "customer"
    ? `Customer scan from ${when}`
    : `scan from ${when}`;
}

/** Hover detail: the landing date as supporting evidence, plus the
 *  provenance caveat — legacy pointers predate the #495 stamps and the
 *  auto-published-vs-Patriot-approved axis is genuinely unknowable for
 *  them, so the tooltip says so rather than letting the pill claim
 *  more than the data knows. */
// `row` is OPTIONAL and last, so every existing caller and test is
// unchanged: only all_reviews_complete reads it, for the count and the
// last review date that stand behind its claim.
export function readinessTitle(readiness, fmtDate, meta, row) {
  if (!readiness || !readiness.state) {
    return "Readiness unavailable this refresh";
  }
  const parts = [];
  if (readiness.landing_date) {
    const when = fmtDate ? fmtDate(readiness.landing_date) : readiness.landing_date;
    parts.push(readiness.state === "in_review"
      ? `Held since ${when}`
      : `Latest scan landed ${when}`);
  }
  // Threshold numbers come from meta (server constants), never
  // literals here — tuning is a backend publish, not a frontend edit.
  if (readiness.state === "ready" && readiness.staleness === "aging") {
    const fresh = meta?.ready_fresh_days;
    const aging = meta?.ready_aging_days;
    parts.push(fresh
      ? `still bookable, but the scan is over ${fresh} days old — worth a glance`
      : aging
        ? `still bookable, but the scan is ${aging}+ days old — worth a glance`
      : "still bookable, but the scan is aging — worth a glance");
  }
  if (readiness.state === "rescan_needed") {
    const rescan = meta?.ready_rescan_days;
    parts.push(rescan
      ? `older than ${rescan} days — book after a fresh scan`
      : "book after a fresh scan");
  }
  if (readiness.state === "fresh_scan") {
    const d = readiness.days_until_next_review;
    const lead = meta?.ready_lead_window_days;
    if (Number.isInteger(d) && Number.isInteger(lead)) {
      parts.push(`${d} days until the next review — can be booked mid-cycle if needed; Ready to book returns inside ${lead} days`);
    } else if (Number.isInteger(d)) {
      parts.push(`${d} days until the next review — can be booked mid-cycle if needed`);
    } else {
      parts.push("outside the scheduling lead window; can be booked mid-cycle if needed");
    }
  }
  if (readiness.state === "contract_expired") {
    parts.push("the contract expiration has passed while the status "
      + "still reads entitled — verify renewal in CRM before anything else");
  }
  // THE COUNT AND THE DATE, from fields the row already carries — the
  // pill states a conclusion ("all of them are done") and this is the
  // arithmetic behind it, so a reader never has to take the pill's word.
  //
  // Both halves degrade independently: an unpopulated allowance yields
  // no count line at all rather than "4 of undefined", and the backend
  // has ALREADY declined to fire the state on a fallback allowance
  // (R2), so a row reaching here without one is a row whose denominator
  // is real but absent from this payload.
  if (readiness.state === "all_reviews_complete") {
    const n = row?.review_number;
    const cap = row?.reviews_per_year;
    if (Number.isInteger(n) && Number.isInteger(cap)) {
      parts.push(`${n} of ${cap} delivered`);
    }
    const last = row?.last_review?.review_date;
    if (last) parts.push(`last ${fmtDate ? fmtDate(last) : last}`);
    parts.push("nothing to book until a renewal is recorded in CRM");
  }
  if (readiness.provenance === "legacy") {
    parts.push("provenance: legacy (pre-#495 pointer)");
  }
  return parts.join(" · ");
}

/** The "Ready to book only" filter. Strict: null readiness is NOT
 *  ready — a row we could not classify must never pass a filter whose
 *  entire job is confidence. AGING rows pass (they are still ready);
 *  rescan_needed rows do not — that falls out of it being its own
 *  state rather than a modifier on ready.
 *
 *  ALREADY-BOOKED ROWS DO NOT PASS (2026-08-11). This is Amber's working
 *  filter — she ticks it to find work — and a row with a booking link already
 *  out or a meeting already on the calendar is not work. The predicate is
 *  isBookingOverridden, the same one the pill and the sort use, so the filter
 *  can never disagree with the label the row is displaying.
 *
 *  It applies to Backlog, Approaching AND the grace section, because one
 *  `readyOnly` state drives all three (the component keeps a single flag so
 *  the three lists cannot disagree). That is the intent: one filter, one
 *  meaning, wherever it is applied. */
export function filterReadyOnly(rows, readyOnly) {
  if (!readyOnly) return rows || [];
  return (rows || []).filter(
    r => r?.readiness?.state === "ready" && !isBookingOverridden(r),
  );
}

/* ── Scheduling filter chips (2026-08-13) ─────────────────────────────
 *
 * Amber, on the call: "can I have a filter where I can filter the readiness…
 * all of the green that are ready to book, so I know who I can send booking
 * links to", and "readiness and then also the scan method? So then it's
 * green, green, green… this is ready to book."
 *
 * DISPLAY-ONLY. Nothing here writes, nothing here fetches, and nothing here
 * changes a count that already existed — the chips narrow the list the
 * current bucket/universe filter is already showing, and that is all.
 *
 * THE IDENTITY RULE (#532/#534) is the whole design. Chip membership comes
 * from readinessPillForRow — THE SAME function the Readiness cell renders
 * with, read through its `key` — so the chip a row matches is always the
 * pill that row is wearing. There is no second derivation to drift, and no
 * second palette: a chip's dot takes the PILL'S OWN TONE, resolved by the
 * component through the one tone→colour map the pill itself uses.
 *
 * The groups OR within themselves and AND across each other, the
 * management-facet semantics one step wider (there, a group held a single
 * value; here Amber wanted to gather several readiness states at once).
 */

/** A row's readiness chip key — the pill's identity, never recomputed.
 *  One line, and it is deliberately one line: the moment this function
 *  decides anything for itself, the chip and the pill can disagree. */
export function readinessChipKey(row) {
  return readinessPillForRow(row).key;
}

/** A row's scan-method chip key. The wire value, with the blank normalised
 *  to a REAL key rather than dropped: "we have never classified this
 *  customer's scan tool" is a population Amber can act on (it is the
 *  migration worklist), and a row with no key would otherwise be
 *  unreachable behind every chip at once. */
export const SCAN_METHOD_CHIP_UNCLASSIFIED = "unclassified";

export function scanMethodChipKey(row) {
  return row?.scan_tool_key || SCAN_METHOD_CHIP_UNCLASSIFIED;
}

export const ALLOWANCE_MODEL_CHIP_UNAVAILABLE = "unavailable";

export function allowanceModelChipKey(row) {
  if (row?.allowance_model === "banked") return "banked";
  if (row?.allowance_model === "legacy") return "legacy";
  return ALLOWANCE_MODEL_CHIP_UNAVAILABLE;
}

export const CHIP_GROUP_READINESS = "readiness";
export const CHIP_GROUP_METHOD = "method";
export const CHIP_GROUP_ALLOWANCE = "allowance";
export const CHIP_GROUPS = [
  CHIP_GROUP_READINESS, CHIP_GROUP_METHOD, CHIP_GROUP_ALLOWANCE,
];

const CHIP_KEY_FN = {
  [CHIP_GROUP_READINESS]: readinessChipKey,
  [CHIP_GROUP_METHOD]: scanMethodChipKey,
  [CHIP_GROUP_ALLOWANCE]: allowanceModelChipKey,
};

/** A row's key in one chip group. */
export function schedulingChipKey(row, group) {
  const fn = CHIP_KEY_FN[group];
  return fn ? fn(row) : null;
}

/** CHIP ORDER, readiness group. The readiness SORT's actionable-first order
 *  (READINESS_SORT_RANK), so the chip row and the sorted list read in the
 *  same direction, with the pills that order cannot see slotted where they
 *  belong:
 *
 *   * ready_aging directly under ready — same state, one tone along;
 *   * rebook beside them, because a reschedule row IS ready (its readiness
 *     state is `ready`) and readinessRank already ranks it there;
 *   * the booking overrides as a block above contract_expired, matching the
 *     single "booked" rank the sort gives them.
 *
 *  A key present in the data but absent here still gets a chip, after the
 *  known ones — an unrenderable population would be invisible while still
 *  counting toward the total, which is how a count silently stops adding up. */
export const READINESS_CHIP_ORDER = [
  "ready",
  READINESS_CHIP_READY_AGING,
  READINESS_CHIP_REBOOK,
  "in_review",
  "rescan_needed",
  "waiting_on_scan",
  "fresh_scan",
  "no_usable_data",
  READINESS_CHIP_UNKNOWN,
  "booking_sent",
  "booked",
  "access_meeting",
  // Beside contract_expired at the tail, matching READINESS_SORT_RANK's
  // 7.8 — the two "do not chase" chips read together.
  "all_reviews_complete",
  "contract_expired",
];

/** CHIP ORDER, scan method. SecureScan365 first — it is the destination of
 *  the migration and the half of the fleet Amber's question is usually
 *  about. SCAN_METHOD_RANK's order for the rest, unclassified last. */
export const SCAN_METHOD_CHIP_ORDER = [
  "securescan365", "legacy", "legacy-auditor", SCAN_METHOD_CHIP_UNCLASSIFIED,
];

/** CHIP ORDER, allowance model. The backend emits only the normalized
 *  allowance_model vocabulary on Scheduling rows. Missing or invalid API
 *  values stay visible as Unavailable rather than being guessed as Legacy:
 *  the raw Dataverse blank-to-Legacy rule has already run server-side. */
export const ALLOWANCE_MODEL_CHIP_ORDER = [
  "banked", "legacy", ALLOWANCE_MODEL_CHIP_UNAVAILABLE,
];

const CHIP_ORDER = {
  [CHIP_GROUP_READINESS]: READINESS_CHIP_ORDER,
  [CHIP_GROUP_METHOD]: SCAN_METHOD_CHIP_ORDER,
  [CHIP_GROUP_ALLOWANCE]: ALLOWANCE_MODEL_CHIP_ORDER,
};

/** Every key any group can legitimately hold. The persistence coercion
 *  validates against THIS, not against the current fleet — a chip naming a
 *  state no row happens to wear today is a valid selection that renders at
 *  count 0, while a key from another release is dropped (#532's lesson). */
export function schedulingChipVocabulary(group) {
  return CHIP_ORDER[group] || [];
}

/** {group: {key: {count, tone}}} over the given rows — the ONE count every
 *  chip in every group reads, and THE TONE THE ROWS THEMSELVES ARE WEARING.
 *
 *  Taking the dot's tone from a real row's pill rather than from a second
 *  table is what makes the colour exact BY CONSTRUCTION: there is no chip
 *  palette to keep in sync with the pill palette, because there is no chip
 *  palette. A tone added to a pill tomorrow reaches its chip with no edit
 *  here, and a chip can never show a colour no row is wearing.
 *
 *  Method and allowance chips carry no tone — their key IS the server-owned
 *  normalized fact, and the component renders them through the same badge /
 *  tag vocabulary the columns use.
 *
 *  Fed the rows the current bucket/universe filter already shows, so a chip
 *  can never resurrect a row the view suppressed. Not zero-filled: a key
 *  with no rows has no entry, and schedulingChipCards decides what renders. */
export function schedulingChipCounts(rows) {
  const counts = {
    [CHIP_GROUP_READINESS]: {},
    [CHIP_GROUP_METHOD]: {},
    [CHIP_GROUP_ALLOWANCE]: {},
  };
  const bump = (bucket, key, tone) => {
    const entry = bucket[key] || (bucket[key] = { count: 0, tone });
    entry.count += 1;
  };
  for (const row of rows || []) {
    if (!row) continue;
    // ONE call, and both facts come out of it — the key the chip filters on
    // and the tone the chip is coloured with are the same pill the Readiness
    // cell is about to render for this row.
    const pill = readinessPillForRow(row);
    bump(counts[CHIP_GROUP_READINESS], pill.key, pill.tone);
    bump(counts[CHIP_GROUP_METHOD], scanMethodChipKey(row), null);
    bump(counts[CHIP_GROUP_ALLOWANCE], allowanceModelChipKey(row), null);
  }
  return counts;
}

/** Apply the active chips. `active` is
 *  {readiness: [...], method: [...], allowance: [...]}.
 *
 *  WITHIN a group the keys OR (Amber gathers several readiness states into
 *  one worklist); ACROSS groups they AND. An empty or absent group means no
 *  filter for that group, so the no-chips call returns everything —
 *  byte-identical to today's list. */
export function filterSchedulingChips(rows, active) {
  return (rows || []).filter(row => CHIP_GROUPS.every(group => {
    const want = active?.[group];
    if (!want || want.length === 0) return true;
    return want.includes(schedulingChipKey(row, group));
  }));
}

/** Is anything selected at all? Drives the Clear affordance and the honest
 *  empty state, so both read one answer rather than each testing the shape. */
export function anySchedulingChipActive(active) {
  return CHIP_GROUPS.some(g => (active?.[g] || []).length > 0);
}

/** Toggle one key in one group, returning a NEW active object. */
export function toggleSchedulingChip(active, group, key) {
  const current = active?.[group] || [];
  return {
    ...active,
    [group]: current.includes(key)
      ? current.filter(k => k !== key)
      : [...current, key],
  };
}

export const EMPTY_SCHEDULING_CHIPS = {
  [CHIP_GROUP_READINESS]: [],
  [CHIP_GROUP_METHOD]: [],
  [CHIP_GROUP_ALLOWANCE]: [],
};

/** The readiness chip's WORD. Assembled from the constants the pill labels
 *  itself from — READINESS_META, BOOKING_PILL_OVERRIDES, REBOOK_PILL_LABEL —
 *  so a copy edit to a pill moves its chip with it. Two keys need a word the
 *  pill does not have, and both are stated here rather than invented at a
 *  render site:
 *
 *   * READY_AGING. The pill says "Ready to book" for fresh AND aging and
 *     separates them by TONE alone, which works in a column of coloured
 *     pills and does not work as two chips reading the same three words. The
 *     parenthetical names the split the colour is already making; the
 *     tooltip on the row still carries the threshold, from meta.
 *   * UNKNOWN. The pill renders "—", which is honest in a narrow column and
 *     unusable as the label of a filter. This is readinessTitle's sentence,
 *     shortened. */
export const READINESS_CHIP_AGING_LABEL = `${READINESS_META.ready.label} (aging scan)`;
export const READINESS_CHIP_UNKNOWN_LABEL = "Readiness unavailable";

export function readinessChipLabel(key) {
  if (key === READINESS_CHIP_REBOOK) return REBOOK_PILL_LABEL;
  if (key === READINESS_CHIP_READY_AGING) return READINESS_CHIP_AGING_LABEL;
  if (key === READINESS_CHIP_UNKNOWN) return READINESS_CHIP_UNKNOWN_LABEL;
  const override = Object.values(BOOKING_PILL_OVERRIDES).find(o => o.key === key);
  if (override) return override.label;
  // An unmapped key renders as itself rather than as a blank chip: a
  // population with no word is a population nobody can act on, and the raw
  // key at least names it.
  return READINESS_META[key]?.label || key;
}

export function allowanceModelChipLabel(key) {
  if (key === "banked") return "Banked";
  if (key === "legacy") return "Legacy";
  if (key === ALLOWANCE_MODEL_CHIP_UNAVAILABLE) return "Unavailable";
  return key || "Unavailable";
}

/* ── Booking-ledger admin visibility (booking-allowance-model §9) ─────
 *
 * PATRIOT-ONLY, DISPLAY-ONLY. Nothing here writes and nothing here fetches;
 * every value is derived from `row.booking`, which the Patriot-gated review
 * -ops payload carries and no customer response does (§8e).
 *
 * The row.booking shape, straight from the backend reducer:
 *   {open_request_on, scheduled_start, reschedule_count, last_rescheduled_on}
 *   plus late_reschedule when the hook has persisted a reschedule verdict.
 * Absent (null) on every row until the ledger has rows — the deploy-day
 * state, in which none of these chips fire.
 *
 * COUNT DISCIPLINE (#557 / #532), non-negotiable: every derivation here is a
 * PREDICATE over a row. The chip count is the length of the filtered list it
 * opens, because it IS that list — there is no separately computed number to
 * drift, which is the whole failure mode those two issues were about.
 */

/** A Requested booking nobody has scheduled after this many days joins the
 *  attention surface (D-6, the goal statement's own proposal).
 *
 *  A MISSED REQUEST MUST NOT ROT behind a greyed customer button: the
 *  customer has asked, the button is now disabled by the double-request
 *  guard, and without this the row looks exactly like one that never asked. */
export const REQUEST_STALE_DAYS = 5;

/** Days a review plausibly needs to be scheduled and delivered (D-7).
 *
 *  The catch-up chip DERIVES the goal statement's "2 left, <=60 days" from
 *  this rather than hardcoding 60, so it stays correct for a customer with 3
 *  or 4 remaining. */
export const CATCHUP_DAYS_PER_REVIEW = 30;

/** Whole days from an ISO instant to now. Null when unparseable.
 *
 *  NOT a DateOnly helper and deliberately not routed through
 *  utils/dates.js: pc_requestedon / pc_lastrescheduledon are DateAndTime +
 *  UserLocal ([V] capture 2026-08-19), which is exactly the case the
 *  date-authority rule does NOT cover. The DateOnly columns on this row
 *  still go through fmtDateOnly/parseDateOnly untouched. */
export function daysSinceInstant(iso, now = new Date()) {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  return Math.floor((now.getTime() - then.getTime()) / 86400000);
}

/** Does this row have an OPEN customer request nobody has booked yet? */
export function hasOpenRequest(row) {
  return Boolean(row?.booking?.open_request_on);
}

/** Age in days of the open request, or null when there is none. */
export function requestAgeDays(row, now = new Date()) {
  if (!hasOpenRequest(row)) return null;
  return daysSinceInstant(row.booking.open_request_on, now);
}

/** The LOUDNESS SAFETY NET: an open request older than REQUEST_STALE_DAYS
 *  with no meeting on the books. */
export function isRequestStale(row, now = new Date()) {
  if (!hasOpenRequest(row)) return false;
  if (row?.booking?.scheduled_start) return false;
  const age = requestAgeDays(row, now);
  return Number.isInteger(age) && age >= REQUEST_STALE_DAYS;
}

/** Reviews still owed in the recorded term. Prefer the backend's
 *  adjustment-aware term value; fall back to the old year-shaped position
 *  only when the server has not supplied one. */
export function reviewsRemaining(row) {
  if (
    row?.allowance_authority?.authority === "banked_transition"
    && row.allowance_authority.status === "error"
  ) {
    return null;
  }
  if (Number.isInteger(row?.reviews_remaining_in_term)) {
    return Math.max(0, row.reviews_remaining_in_term);
  }
  const perYear = row?.reviews_per_year;
  const number = row?.review_number;
  if (perYear == null || number == null) return null;
  const used = Math.max(0, Number(number) - 1);
  return Math.max(0, Number(perYear) - used);
}

/** Days left in the recorded term, from the backend-derived term end. */
export function daysLeftInTerm(row, now = new Date()) {
  const end = row?.term_end_date;
  if (!end) return null;
  const parsed = parseDateOnly(end);
  if (!parsed) return null;
  return Math.ceil((parsed.getTime() - now.getTime()) / 86400000);
}

/** THE CATCH-UP COHORT (R5): a customer whose remaining allowance does not
 *  plausibly fit the days left in their term.
 *
 *  `days_left <= remaining * 30` — `<=`, amended per REVIEW B-4, because `<`
 *  did not fire at exactly 60 days with 2 remaining, which is the goal
 *  statement's own worked example. One character, and it matters because
 *  this is the view James works BEFORE flipping a customer's model.
 *
 *  Silent (false) whenever either input is unknown: an unknown allowance
 *  cannot support a claim that a customer is behind. */
export function isCatchUpCohort(row, now = new Date()) {
  const remaining = reviewsRemaining(row);
  const daysLeft = daysLeftInTerm(row, now);
  if (remaining == null || daysLeft == null) return false;
  if (remaining <= 0) return false;
  if (daysLeft < 0) return false;
  return daysLeft <= remaining * CATCHUP_DAYS_PER_REVIEW;
}

/** Hours of notice the most recent reschedule gave, when the backend
 *  supplies the old-start calculation directly. */
export function rescheduleNoticeHours(row) {
  return Number.isFinite(row?.booking?.reschedule_notice_hours)
    ? row.booking.reschedule_notice_hours
    : null;
}

/** Mirrors review_cycle.RESCHEDULE_FREE_HOURS. Duplicated across the wire
 *  boundary rather than shipped in the payload because it is a POLICY
 *  CONSTANT, not row data — and the backend copy stays the authority: this
 *  one only decides whether a row is worth showing Amber. */
export const RESCHEDULE_FREE_HOURS = 48;

/** A LATE RESCHEDULE — moved inside the free window (§7c item 3).
 *
 *  DISPLAY ONLY, AND IT CONSUMES NOTHING. It surfaces the classifier's
 *  recommendation so the operator's send_booking_link choice at Review
 *  Complete is INFORMED rather than guessed. Consumption still happens
 *  exactly where it happens today — an operator recording the no-show
 *  through Review Complete without a booking link (D-9). */
export function isLateReschedule(row) {
  return row?.booking?.late_reschedule === true;
}

/** The booking chips, each a {key, label, predicate} triple.
 *
 *  ONE PREDICATE PER CHIP, and the count is the length of the list the
 *  predicate produces — so "any count opens the list it counts" is true by
 *  construction rather than by discipline. */
export const BOOKING_CHIP_OPEN_REQUEST = "open_request";
export const BOOKING_CHIP_STALE_REQUEST = "stale_request";
export const BOOKING_CHIP_CATCH_UP = "catch_up";
export const BOOKING_CHIP_LATE_RESCHEDULE = "late_reschedule";

/* `tone` is a PALETTE KEY, not a semantic name — the same vocabulary the
 * scan-method badges pass to badgeColors(type, weight). A semantic word
 * like "warn" is not in either palette and would silently resolve to the
 * blue fallback, which is precisely "a chip wearing a colour no row wears".
 *
 * AMBER IS SOFT-ONLY, ALWAYS. `amber` exists in the soft palette and NOT in
 * the heavy one, so a heavy amber chip renders BLUE. Every amber entry here
 * therefore carries weight "soft", and the test asserts it.
 *
 * The stale-request chip is the one deliberate escalation: heavy red, which
 * DOES exist. A customer request nobody answered for REQUEST_STALE_DAYS is
 * the loudest thing this group can say, and it should not look the same as
 * the request that arrived this morning. */
export const BOOKING_CHIP_DEFS = [
  {
    key: BOOKING_CHIP_OPEN_REQUEST,
    label: "Review requested",
    tone: "amber",
    weight: "soft",
    predicate: hasOpenRequest,
  },
  {
    key: BOOKING_CHIP_STALE_REQUEST,
    label: `Request unanswered ${REQUEST_STALE_DAYS}+ d`,
    tone: "red",
    weight: "heavy",
    predicate: isRequestStale,
  },
  {
    key: BOOKING_CHIP_CATCH_UP,
    label: "Catch-up needed",
    tone: "amber",
    weight: "soft",
    predicate: isCatchUpCohort,
  },
  {
    key: BOOKING_CHIP_LATE_RESCHEDULE,
    label: "Late reschedule",
    tone: "amber",
    weight: "soft",
    predicate: isLateReschedule,
  },
];

/** Rows matching one booking chip. THE list its count counts. */
export function filterBookingChip(rows, key, now = new Date()) {
  const def = BOOKING_CHIP_DEFS.find(d => d.key === key);
  if (!def) return [];
  return (rows || []).filter(row => row && def.predicate(row, now));
}

/** [{key, label, tone, weight, count}] over the rows the current view is
 *  ALREADY showing — so a chip can never resurrect a row the view
 *  suppressed, the same rule the readiness chips follow.
 *
 *  Zero-count chips are RETAINED, not dropped: "nobody is waiting on a
 *  review request" is a real and reassuring answer, and a chip that vanishes
 *  cannot say it. */
export function bookingChipCards(rows, now = new Date()) {
  return BOOKING_CHIP_DEFS.map(def => ({
    key: def.key,
    label: def.label,
    tone: def.tone,
    weight: def.weight,
    count: filterBookingChip(rows, def.key, now).length,
  }));
}

/** The chips for one group, in order, each {group, key, count, tone, active}.
 *  `tone` is the pill tone of the rows behind the chip, or null when there
 *  are none — the component resolves it through the SAME tone→colour map the
 *  pill renders with, and dims a chip that has no rows. A zero chip has no
 *  colour to show because no row is wearing one, which is the honest answer
 *  rather than an arbitrary one.
 *
 *  THE RENDER RULES, each earning its place (the managementFacetCards rules,
 *  and the same reasoning):
 *
 *   * a key with rows renders;
 *   * a key with NO rows does not — the readiness vocabulary is thirteen
 *     wide and most of it is empty in any one bucket, so zero chips there
 *     are noise;
 *   * an ACTIVE key ALWAYS renders, even at zero. This is the requirement
 *     that a persisted chip naming a state no longer present shows a 0
 *     rather than vanishing: an invisible active filter is the worst state
 *     this control can reach, because the list is short and nothing on
 *     screen says why. */
export function schedulingChipCards(group, counts, activeKeys) {
  const groupCounts = counts?.[group] || {};
  const order = CHIP_ORDER[group] || [];
  const active = activeKeys || [];
  const extras = Object.keys(groupCounts).filter(k => !order.includes(k));
  return [...order, ...extras.sort()]
    .map(key => ({
      group, key,
      count: groupCounts[key]?.count || 0,
      tone: groupCounts[key]?.tone || null,
      active: active.includes(key),
    }))
    .filter(chip => chip.count > 0 || chip.active);
}

/* ── Chip persistence ────────────────────────────────────────────────
 * The loadRowSort / saveRowSort helper stores ONE string per view and is
 * already wrapped against localStorage throwing (private-mode Safari, some
 * embedded webviews) — a filter preference must never break the tab. Each
 * group's keys are encoded into that one string rather than reaching for a
 * second storage mechanism.
 *
 * COERCED ON THE WAY IN AND OUT, the schedulingFilter lesson: a stored value
 * is user-writable and outlives the release that wrote it. Unknown keys are
 * dropped against the fixed vocabulary — NOT against the current fleet, so a
 * valid state nobody is wearing today survives a reload and renders at 0. */
export const SCHEDULING_CHIP_VIEW = "schedulingChips";

const CHIP_GROUP_CODE = {
  [CHIP_GROUP_READINESS]: "r",
  [CHIP_GROUP_METHOD]: "m",
  [CHIP_GROUP_ALLOWANCE]: "a",
};

export function serializeSchedulingChips(active) {
  return CHIP_GROUPS
    .map(g => [CHIP_GROUP_CODE[g], (active?.[g] || []).join(",")])
    .filter(([, keys]) => keys)
    .map(([code, keys]) => `${code}=${keys}`)
    .join(";");
}

export function parseSchedulingChips(raw) {
  const parsed = { ...EMPTY_SCHEDULING_CHIPS };
  const byCode = Object.fromEntries(
    CHIP_GROUPS.map(g => [CHIP_GROUP_CODE[g], g]));
  for (const part of String(raw || "").split(";")) {
    const [code, keys] = part.split("=");
    const group = byCode[code];
    if (!group || !keys) continue;
    const allowed = schedulingChipVocabulary(group);
    // Deduplicated as well as filtered: a hand-edited value could repeat a
    // key, and a repeated key would double-count nothing but would render
    // the chip twice.
    parsed[group] = [...new Set(keys.split(",").filter(k => allowed.includes(k)))];
  }
  return parsed;
}

export function loadSchedulingChips() {
  return parseSchedulingChips(loadRowSort(SCHEDULING_CHIP_VIEW));
}

export function saveSchedulingChips(active) {
  saveRowSort(SCHEDULING_CHIP_VIEW, serializeSchedulingChips(active));
}

// ── Expired-but-entitled sweep (2026-08-09) ──────────────────────────
// The server flags rows whose contract expiration sits in the past
// while the status still reads entitled (contract_expired /
// expired_days / expiration_date — computed against the caller's
// today, never here). The flag ANNOTATES: rows keep their buckets.
// The action is a CRM correction (record the renewal or close the
// row) — the account side's loop, not Amber's and not the engineers'.

/** Count for the banner — an aggregation of server flags, never a
 *  re-derivation. */
export function contractExpiredCount(rows) {
  return (rows || []).filter(r => r?.contract_expired).length;
}

/** The banner's click-to-filter. */
export function filterContractExpired(rows, expiredOnly) {
  if (!expiredOnly) return rows || [];
  return (rows || []).filter(r => r?.contract_expired);
}

/** The Contract cell annotation: "Ongoing — expired 6/12" replaces the
 *  bare status when flagged; null when not (render the plain status). */
/* ── Contract status: wire vocabulary vs display label ───────────────
   The backend emits SEVEN status strings, mapped from the Dataverse choice
   INTEGER by its own hardcoded `_CONTRACT_STATUS_LABELS`. Mirrored here as a
   LIST, not imported from a file the frontend cannot reach — the
   KNOWN_DATE_RULES pattern (#535). The guard test asserts that every string
   the universes name is one the backend can actually emit, and that every
   wire label has a decided display answer, so a backend label edit becomes a
   red test rather than a silently empty universe in production. */
export const KNOWN_CONTRACT_STATUS = [
  "Ongoing", "On Hold", "Closed", "Open", "Expired", "Pending", "New",
];

/** Every wire label the universes are built from, exported for the guard.
 *  Not used for filtering — `filterSchedulingUniverse` owns that. */
export const CONTRACT_UNIVERSE_LABELS = [...DEFAULT_UNIVERSE, ...REDUCED_UNIVERSE];

/** Wire label → what a human reads. ONE entry, and one statement of the
 *  rule: the CRM choice for 125470003 was renamed to "One Time" while the
 *  wire label stayed "Open" (see the DEFAULT_UNIVERSE note). "Open" reads
 *  like just another active customer, which is the confusion being fixed;
 *  the engagement is a single audit delivery, so it says so. */
const CONTRACT_DISPLAY_LABELS = { Open: "One Time" };

/** The label to RENDER for a contract status. Every other wire label passes
 *  through untouched — including the empty/absent case, which stays falsy so
 *  callers keep their own "—" fallback. */
export function contractDisplayLabel(status) {
  if (!status) return status;
  return CONTRACT_DISPLAY_LABELS[status] || status;
}

/* ── The Contract chip's colour register (operator decision 2026-08-11) ──
 *
 * The chip was slate for all seven statuses, so the one column that carries
 * a customer's contract STATE said it in text only. This is the
 * WORKFLOW_TONE_BY_VALUE shape: one map, keyed on the wire label, valued
 * with an EXISTING soft-palette key. No new colour values, and this module
 * never holds a hex.
 *
 * THE ASSIGNMENTS, and what each one means here:
 *   Ongoing   green    healthy and active — the resting state of the book
 *   Open      teal     One Time. The label already reads "One Time"; teal is
 *                      the engagement-type accent it shares with the
 *                      customer-managed-solution tag elsewhere
 *   On Hold   amber    paused, waiting on something — the same meaning amber
 *                      carries in the workflow register
 *   Pending   indigo   not started yet
 *   New       indigo   likewise: known, not yet running
 *   Expired   slate    THE RED FOR THESE ROWS LIVES ON THE RENEWAL PILL
 *   Closed    slate    beside it. Urgency is the pill's channel, and a red
 *                      chip would compete with it for the same eye, on the
 *                      same row, saying the same thing twice
 *
 * CROSS-COLUMN COLLISIONS, decided rather than overlooked (the
 * readiness-pill discipline): green also means "Ready to book" in the
 * Scheduling view's Readiness column, teal also marks a customer-managed
 * solution on the recommendations surface, and amber means "blocked" in the
 * workflow-status register. Every one of those is a DIFFERENT COLUMN with a
 * DIFFERENT LABEL in the cell — "Ongoing" is never mistakable for "Ready to
 * book" — and this view renders none of those columns at all. An ambiguity
 * needs two competing readings in one place; there are none here.
 *
 * An unrecognised or absent status takes slate: a status nobody has
 * classified must not be painted as something it is not (the workflowTone
 * "unset" rule). */
export const CONTRACT_STATUS_TONES = {
  Ongoing: "green",
  Open: "teal",
  "On Hold": "amber",
  Pending: "indigo",
  New: "indigo",
  Expired: "slate",
  Closed: "slate",
};

/** The soft Badge tone for a row's contract status. */
export function contractStatusTone(row) {
  return CONTRACT_STATUS_TONES[row?.contract_status] || "slate";
}

/** Is this a One Time audit engagement?
 *
 *  READS CONTRACT STATUS, NEVER TIER. One Time engagements are Bronze by
 *  business policy (operator ruling 2026-08-11), but tier is a pricing fact
 *  that Ongoing customers also carry — deriving the engagement type from it
 *  would call every Bronze customer a one-timer. The policy runs one way
 *  only, and this is the single statement of the rule. */
export function isOneTime(row) {
  return row?.contract_status === "Open";
}

/** Wire labels for the TERMINAL contract statuses. Expired is becoming the
 *  single terminal status for every contract (operator policy 2026-08-11);
 *  Closed is the legacy value and rows still carry it, so both are named
 *  rather than leaving the older one to behave differently by omission.
 *  Mirrors shared/customer_management.TERMINAL_STATUSES — the
 *  KNOWN_CONTRACT_STATUS mirroring idiom, and the guard test asserts every
 *  entry is a label the backend can actually emit. */
export const TERMINAL_CONTRACT_STATUS = ["Expired", "Closed"];

/** Is this a One Time engagement that is OVER?
 *
 *  THE SECOND HALF OF THE SAME ONE-WAY POLICY as isOneTime above, and it is
 *  a DIFFERENT test, which is why it is its own helper rather than a flag on
 *  that one. isOneTime reads contract_status === "Open" — the ACTIVE
 *  one-timer, tier never consulted. A finished one is terminal, and the
 *  status alone cannot say it: an Expired GOLD customer is a lapsed ongoing
 *  contract (Katelyn's hottest renewal conversation), so tier is
 *  load-bearing here and only here. "One Time = Bronze" runs one way —
 *  Bronze does NOT mean One Time, since Ongoing customers carry tiers too.
 *
 *  The consequence on screen: nothing to renew and nobody to chase, so the
 *  row reads a muted "One Time · Done" and NEVER the red pill its expired
 *  status would otherwise earn (operator decision 2026-08-11). */
export function isFinishedOneTime(row) {
  return TERMINAL_CONTRACT_STATUS.includes(row?.contract_status)
    && row?.tier === "Bronze";
}

export function contractCellLabel(row, fmtDate) {
  if (!row?.contract_expired) return null;
  const status = contractDisplayLabel(row.contract_status) || "—";
  const when = row.expiration_date
    ? (fmtDate ? fmtDate(row.expiration_date) : row.expiration_date)
    : null;
  return when ? `${status} — expired ${when}` : `${status} — expired`;
}

/** Hover detail for the Contract cell when flagged. */
export function contractCellTitle(row) {
  if (!row?.contract_expired) return undefined;
  const days = row.expired_days;
  const lead = Number.isInteger(days)
    ? `Contract expiration passed ${days} day${days === 1 ? "" : "s"} ago`
    : "Contract expiration has passed";
  return `${lead} while the status still reads entitled — verify renewal `
    + "in CRM (record the new expiration, or close the row).";
}

// ── Unrecorded-review flag (2026-08-09) ──────────────────────────────
// Server-derived (Meeting Scheduled + date past the grace); the
// frontend never re-derives — it renders row.unrecorded_review and
// aggregates it. Chase the ENGINEER, not the customer: the fix is a
// Review Complete press that already exists on the customer page.
// Independent of contract_expired by design — different actions for
// different people; a dual-flagged row counts in BOTH banners and
// renders BOTH annotations.

/** Banner count — aggregation of server flags, never a re-derivation. */
export function unrecordedCount(rows) {
  return (rows || []).filter(r => r?.unrecorded_review).length;
}

/** The banner's click-to-filter. */
export function filterUnrecorded(rows, unrecordedOnly) {
  if (!unrecordedOnly) return rows || [];
  return (rows || []).filter(r => r?.unrecorded_review);
}

/** Row badge label; null when the row isn't flagged. */
export function unrecordedBadge(row) {
  return row?.unrecorded_review ? "likely delivered — record it" : null;
}

/** Badge tooltip. Grace comes from meta (server constant), never a
 *  literal here. */
export function unrecordedTitle(row, meta) {
  if (!row?.unrecorded_review) return undefined;
  const days = row.days_overdue;
  const grace = meta?.unrecorded_grace_days;
  const lead = Number.isInteger(days)
    ? `Meeting Scheduled date passed ${days} day${days === 1 ? "" : "s"} ago`
    : "The Meeting Scheduled date has passed";
  const graceNote = Number.isInteger(grace)
    ? ` (grace ${grace} days)` : "";
  return `${lead}${graceNote} — the review almost certainly happened and `
    + "was never recorded. Chase the engineer, not the customer: press "
    + "Review Complete on the customer page.";
}

// ── Row sorting for the Backlog / Approaching tables (2026-08-10) ────
// Sorting applies WITHIN a section, never across: the caller sorts the
// backlog rows and the "Just past due" rows separately, so the two
// sections can never merge (the grace rows carry no severity and a
// 3-day-old row dropped into a longest-overdue-first sequence destroys
// what that order means).

// Readiness has no natural order, so one is defined here: ACTIONABLE
// FIRST — what can Amber do something about right now (operator
// decision 2026-08-10).
//   1 ready               book it
//   2 in_review           a Patriot publish unblocks it
//   3 rescan_needed       ask the customer for a fresh scan
//   4 waiting_on_scan     nothing to do but wait
//   4.2 fresh_scan        fresh data, but not inside the Legacy lead window
//   5 no_usable_data      needs investigation / a CRM fix
//   7 (unknown)           readiness unavailable this refresh — "—"
//   8 contract_expired    DO NOT CHASE: last regardless of anything else
// rescan_needed was not in the operator's stated list (it postdates it,
// #506); placed above waiting_on_scan because it is actionable — ask for a scan —
// where waiting_on_scan is not.
export const READINESS_SORT_RANK = {
  ready: 1,
  in_review: 2,
  rescan_needed: 3,
  waiting_on_scan: 4,
  fresh_scan: 4.2,
  no_usable_data: 5,
  // Nothing to do until a renewal is recorded — known-not-work, like a
  // booked row, and for a contract reason rather than a scan one.
  //
  // 7.8: BELOW booked (7.5) because a booked row's contract is fine and
  // this one's review year is spent, and ABOVE contract_expired (8),
  // which stays "last regardless of anything else" by operator decision.
  // A fraction, exactly as READINESS_RANK_BOOKED is, so no existing rank
  // moves — the same reasoning, applied a second time.
  //
  // It also MIRRORS THE CLASSIFIER'S PRECEDENCE, where this state sits
  // immediately after contract_expired: the sorted list and the pill
  // read in the same direction, which is the whole point of the two
  // tables agreeing.
  all_reviews_complete: 7.8,
  contract_expired: 8,
};
const READINESS_RANK_UNKNOWN = 7;
// Already booked — a booking link out, or a meeting on the calendar
// (2026-08-11). Sits between unknown (7) and contract_expired (8), and is a
// fraction rather than a renumbering so no existing rank moves.
//
// WHY HERE, justified against both neighbours. The list's principle is
// "actionable first", and a booked row is the most definite "nothing to do"
// on it:
//   * BELOW unknown (7) — unknown is a transient gap in this refresh's blob
//     index and the row may turn out to be actionable next time; a booked row
//     is KNOWN not to be work. Known-not-work sorts under maybe-work.
//   * ABOVE contract_expired (8) — rank 8 is "last regardless of anything
//     else" by operator decision and stays that way. The two are also
//     different in kind: contract_expired is a data error wanting a CRM fix,
//     while a booked row is functioning exactly as intended.
// Considered and rejected: 4.5, at the head of the non-actionable block. It
// groups "handled" ahead of "absent or broken", which is coherent, but it
// leaves booked rows in the middle of the list — the opposite of the request
// that prompted this.
// Booking Sent and Booked share one rank: the sort answers "is there work
// here", and both answer no. Splitting them would encode a distinction
// nobody asked for.
const READINESS_RANK_BOOKED = 7.5;

// Migrated first, then legacy, then unclassified — the order the Scan
// Fleet worklist reads in, so "who can I scan" groups together.
// Sort order for the scan-method column: the modern tool first, then the
// legacy route with its two members adjacent, then anything unmapped
// (the ?? 3 fallback below). Legacy - Auditor sits beside Legacy because
// they are one family.
const SCAN_METHOD_RANK = { securescan365: 1, legacy: 2, "legacy-auditor": 3 };

export const ROW_SORT_CUSTOMER = "customer_name";
export const ROW_SORT_READINESS = "readiness";
export const ROW_SORT_SCAN_METHOD = "scan_method";

/** Options for the sort control. The default option's label differs per
 *  view because the server order differs (backlog: longest overdue
 *  first; approaching: soonest first) — the default itself is NOT
 *  changed by this feature. */
export function rowSortOptions(view) {
  return [
    {
      value: "",
      label: view === "approaching"
        ? "Default (soonest due first)"
        : view === "banked"
          ? "Default (outreach timing)"
        : (view === "all" || view === "onhold" || view === "backlogapproaching")
          ? "Default (most urgent first)"
          : "Default (most overdue first)",
    },
    { value: ROW_SORT_CUSTOMER, label: "Customer name (A–Z)" },
    { value: ROW_SORT_READINESS, label: "Readiness (actionable first)" },
    { value: ROW_SORT_SCAN_METHOD, label: "Scan method" },
  ];
}

function readinessRank(row) {
  // contract_expired wins from either source: the readiness state (which
  // #508 made first-precedence) or the row flag, so a row can never sort
  // as actionable because readiness happened to be unavailable.
  if (row?.contract_expired) return READINESS_SORT_RANK.contract_expired;
  // Already booked, below everything still actionable. Checked after the
  // contract flag and before the state, mirroring the pill's own precedence —
  // and through the same predicate, so a status added to
  // BOOKING_PILL_OVERRIDES reaches the pill, the filter and the sort at once.
  // isBookingOverridden is itself false for the unoverridable states, so
  // no_usable_data keeps rank 6 with a booking status set.
  if (isBookingOverridden(row)) return READINESS_RANK_BOOKED;
  const state = row?.readiness?.state;
  if (!state) return READINESS_RANK_UNKNOWN;
  return READINESS_SORT_RANK[state] ?? READINESS_RANK_UNKNOWN;
}

/** Sort ONE section's rows. Returns a new array; the server order is
 *  returned untouched (a copy) when no sort is selected. customer_name
 *  is the tracking row's own name (pc_recordname) — the same field the
 *  Customer column renders, never the parent account name. */
export function sortReviewRows(rows, sortKey) {
  const list = [...(rows || [])].filter(Boolean);
  if (!sortKey) return list;
  const name = (r) => lower(r?.customer_name);
  const rank = (r) => {
    if (sortKey === ROW_SORT_READINESS) return readinessRank(r);
    if (sortKey === ROW_SORT_SCAN_METHOD) {
      return SCAN_METHOD_RANK[r?.scan_tool_key] ?? 4;   // unmapped last
    }
    return 0;
  };
  list.sort((a, b) => {
    if (sortKey !== ROW_SORT_CUSTOMER) {
      const ra = rank(a);
      const rb = rank(b);
      if (ra !== rb) return ra - rb;
    }
    // Customer name is the sort for one option and the stable tiebreak
    // for the others, so equal keys never shuffle between renders.
    const na = name(a);
    const nb = name(b);
    if (na !== nb) return na.localeCompare(nb);
    return lower(a?.slug).localeCompare(lower(b?.slug));
  });
  return list;
}

// Per-tab persistence, so a choice survives a reload. Wrapped because
// localStorage throws in private-mode Safari and in some embedded
// webviews — a sort preference must never break the tab.
const ROW_SORT_STORAGE_PREFIX = "ss365.reviewRowSort.";

export function loadRowSort(view) {
  try {
    return window.localStorage.getItem(ROW_SORT_STORAGE_PREFIX + view) || "";
  } catch (e) {
    return "";
  }
}

export function saveRowSort(view, sortKey) {
  try {
    if (sortKey) window.localStorage.setItem(ROW_SORT_STORAGE_PREFIX + view, sortKey);
    else window.localStorage.removeItem(ROW_SORT_STORAGE_PREFIX + view);
  } catch (e) { /* preference is best-effort */ }
}

// ── The Workflow status select's displayed value (bugfix 2026-08-10) ──
// The select used a permanent `value=""` placeholder whose LABEL is the
// row's SAVED status. Picking a status fires onChange, the pick opens the
// date editor (or the confirm), React re-renders, and the controlled
// value snaps straight back to "" — so the dropdown shows the SAVED
// status again while an editor for a DIFFERENT status is open beneath it.
// The write was never wrong (handleStatusDateSubmit reads
// statusEdit.statusValue, not the select), but the row said one thing and
// meant another, and re-picking to "fix" the display wiped the date the
// recorder had just entered — an inescapable-feeling loop.
//
// The select now shows the PENDING pick while an editor or confirm is
// open for that row, and falls back to the placeholder otherwise.

/** The select's controlled value: the pending pick for this row, else ""
 *  (the placeholder, which renders the saved status label). */
export function pendingStatusValue(slug, statusEdit, statusConfirm) {
  if (statusEdit && statusEdit.slug === slug
      && statusEdit.statusValue != null) {
    return String(statusEdit.statusValue);
  }
  if (statusConfirm && statusConfirm.slug === slug
      && statusConfirm.statusValue != null) {
    return String(statusConfirm.statusValue);
  }
  return "";
}

/** The next statusEdit for a REQUIRE_DATE pick. Re-picking the SAME
 *  status keeps the date already typed — the old code always reset it to
 *  "", so a recorder who picked the date, noticed the dropdown showing
 *  the old status and re-picked lost the date and re-disabled Set. */
export function statusPickUpdate(prevEdit, slug, statusValue) {
  const samePick = prevEdit
    && prevEdit.slug === slug
    && Number(prevEdit.statusValue) === Number(statusValue);
  return {
    slug,
    statusValue,
    date: samePick ? (prevEdit.date || "") : "",
  };
}


// ── Follow-up window progress (2026-08-10) ──────────────────────────
// THE DISPLAY DECISION, and why the "N d" column is NOT switched to
// working days for the three follow-up statuses:
//
//   1. That column IS the default sort key (backlog, longest-overdue
//      first, calendar). Showing working days on some rows and calendar
//      on others makes the visible numbers non-monotonic under the
//      default order — a list sorted by a number you cannot see, which
//      is a worse kind of "reads as broken" than the one we are fixing.
//   2. The confusion is caused by an INVISIBLE RULE, not by the unit. A
//      13 d row sitting in Just past due next to a 12 d row in Backlog
//      is explained by saying which window it is in — not by changing
//      what 13 means.
//   3. Progress through the window ("day 7 of 10 working") is more
//      useful than working days elapsed ("7 wd"): it says how much
//      runway is left, which is what the follow-up cadence is about.
//
// So: one unit in the sortable column (calendar, every row), and an
// explicit second line on the follow-up rows that names the working-day
// window on the row itself — not only in a tooltip.
export function followUpProgressLabel(row, meta) {
  if (row?.grace_basis !== "working") return null;
  const elapsed = row?.working_days_overdue;
  if (!Number.isInteger(elapsed)) return null;
  const window = meta?.backlog_grace_working_days ?? 10;
  if (row?.bucket === "grace") {
    return `day ${Math.min(elapsed, window)} of ${window} working`;
  }
  return `follow-up window closed (${window} working days)`;
}

/** Tooltip for the day-count cell: always states the unit of the number
 *  shown, and for follow-up rows both figures side by side. */
export function dayCountTitle(row, meta) {
  const calendar = row?.days_overdue;
  if (!Number.isInteger(calendar)) return undefined;
  const base = `${calendar} calendar day${calendar === 1 ? "" : "s"} past the next-audit date`;
  if (row?.grace_basis !== "working") return base;
  const elapsed = row?.working_days_overdue;
  const window = meta?.backlog_grace_working_days ?? 10;
  return `${base} — ${elapsed} working day${elapsed === 1 ? "" : "s"} `
    + `(Mon–Thu) of the ${window}-working-day follow-up window`;
}

// ── Customer Management (2026-08-11) ─────────────────────────────────
// The middle verb: Scheduling says "do not chase this row", this says
// "THIS ROW NEEDS FIXING". Audience is Katelyn, and every fix happens in
// CRM — so the tag on a row is an INSTRUCTION, not a diagnosis.
//
// EVERYTHING HERE IS PRESENTATION. The six predicates, the renewal state,
// the severity order and the row order are all derived server-side
// (shared/customer_management.py) and shipped on the row; nothing in this
// section re-derives any of them. A frontend that recomputed a predicate
// would be a second source of truth for the same quantity — the #488
// allowance failure, on a view whose whole job is telling the operator
// which records disagree with each other.

/** Wire predicate name → the tag a reader clicks nothing to understand.
 *
 *  `tag` is short enough for a table cell; `fix` is the tooltip and it says
 *  WHAT TO DO IN CRM, because a list of defects nobody can act on is a
 *  report, not a worklist. Keyed on the server's own predicate names
 *  (customer_management.PROBLEM_ORDER); the guard test asserts every name
 *  the server can emit has an entry here, so adding a predicate without a
 *  label fails the suite instead of rendering a blank tag. */
export const PROBLEM_TAGS = {
  no_term_data: {
    tag: "No term data",
    fix: "Term Start Date or Duration is missing, so the term end cannot be "
      + "derived at all — set both in CRM. Until then the Review Complete "
      + "button will not advance this customer's contract year.",
  },
  expiration_mismatch: {
    tag: "Term End mismatch",
    fix: "The stored Term End Date is a different day from the term derived "
      + "from Term Start Date + Duration, under either fleet convention "
      + "(the anniversary or the day before it). One of the three fields is "
      + "wrong — correct whichever it is in CRM. The derived term is what the "
      + "scheduling arithmetic uses.",
  },
  term_ended: {
    tag: "Term ended",
    fix: "The derived term has already ended while the contract status still "
      + "reads Ongoing or One Time. Record the renewal, or set the status to "
      + "Expired.",
  },
  tsy_above_cy: {
    tag: "Start year > CY",
    fix: "Term Start Year is ahead of Contract Year, which puts the review "
      + "window in the future. Correct whichever of the two is wrong in CRM.",
  },
  autorenew_contradiction: {
    tag: "Auto-renew conflict",
    fix: "The contract reads as over while Auto Renew is still on — both "
      + "cannot be true. Either the renewal happened and needs recording in "
      + "CRM (term fields and status), or the flag is stale and should be "
      + "cleared.",
  },
  term_start_future: {
    tag: "Term starts later",
    fix: "Term Start Date is in the future. If a renewal was recorded early, "
      + "schedule it through Record Renewal instead; otherwise check for a "
      + "mistyped year.",
  },
  awaiting_renewal: {
    tag: "Awaiting renewal",
    fix: "Every review in this contract year is used and the recorded term "
      + "holds no further review year, so the year cannot advance. Record the "
      + "renewal in CRM — the row clears itself the moment you do.",
  },
};

/** The tags for one row, in the SERVER's severity order.
 *
 *  Reads row.problems as given: the server ordered it (PROBLEM_ORDER) and it
 *  is the same order the rows are sorted by, so the first tag on a row is
 *  also the reason that row is where it is. An unrecognised name — a backend
 *  ahead of this deploy — renders its raw name rather than vanishing: an
 *  ugly tag is a question, a missing one is a lie. */
export function problemTags(row, meta) {
  return (row?.problems || []).map(name => ({
    name,
    tag: problemTagText(name, row),
    fix: problemTagFix(name, row, meta),
  }));
}

/** The tag's WORDS, with the mismatch distance appended when the server
 *  measured one: "Term End mismatch · 92 d".
 *
 *  THE NUMBER IS THE MAGNITUDE. Every tagged row is already past the
 *  backend's grace (EXPIRATION_MISMATCH_GRACE_DAYS, 2026-08-12), so the
 *  question it answers is no longer "is this real" but "how wrong is it":
 *  days out points at the stored date, weeks or months at the term fields.
 *
 *  Rendered ONLY when the server sent a number, and NO BANDING happens here:
 *  the tooltip words the two ends and the grace itself comes from meta, so
 *  this side holds no opinion that could drift from the backend's. */
export function problemTagText(name, row) {
  const base = PROBLEM_TAGS[name]?.tag || name;
  if (name !== "expiration_mismatch") return base;
  const days = row?.expiration_mismatch_days;
  return Number.isInteger(days) ? `${base} · ${days} d` : base;
}

/** The tag's tooltip. Mismatch rows get ONE extra sentence of triage
 *  guidance, in words rather than a number, so the reader decides which
 *  band their delta is in — and a future change to what counts as
 *  "month-end" needs no code change on either side. */
export function problemTagFix(name, row, meta) {
  const base = PROBLEM_TAGS[name]?.fix;
  if (name !== "expiration_mismatch" || !base) return base;
  // REWRITTEN 2026-08-12 for the grace. The old sentence began "a day or two
  // apart is almost certainly a month-end recording convention" — guidance
  // about rows that no longer appear at all, which would have sent the reader
  // looking for a case the list cannot contain. Anything TAGGED is already
  // past the grace, so the guidance is now about MAGNITUDE.
  //
  // The grace itself comes from meta (the backlog_grace_days idiom), so the
  // copy can never claim a window the server is not using.
  const grace = meta?.expiration_mismatch_grace_days ?? 7;
  return `${base} Gaps within ${grace} days are month-end recording drift `
    + "and are not flagged, so this one is further out than that: a handful "
    + "of days points at the stored date — correct it. Weeks or more means "
    + "the TERM fields are the likely fault: check Term Start Date and "
    + "Duration before trusting either date, including this row's renewal "
    + "pill.";
}

/** Rows carrying at least one problem — the pinned group.
 *  THE BADGE AND THE HEADER BOTH READ THIS, over the rows the view actually
 *  renders, so the number on the control can never disagree with the list it
 *  opens (#532). */
export function problemRows(rows) {
  return (rows || []).filter(r => (r?.problems || []).length > 0);
}

export function problemRowCount(rows) {
  return problemRows(rows).length;
}

/** The problem-rows-only filter, for the badge's click-through. */
export function filterProblemRows(rows, problemsOnly) {
  if (!problemsOnly) return rows || [];
  return problemRows(rows);
}

// ── The renewal pill ────────────────────────────────────────────────
// Driven by (derived term end − today), computed server-side into
// row.renewal_state. The threshold between far and approaching is a BACKEND
// constant (meta.renewal_approaching_days, the backlog_grace_days idiom):
// tuning it is a publish, never an edit here — so this map carries no
// number at all.
//
// COLOUR MEANS PROXIMITY TO A RENEWAL, nothing else. `done` is the one
// state with no colour, which is the operator's exception made visible: a
// finished One Time engagement has nothing to renew, so a red pill there
// would be an instruction to chase somebody about nothing.
export const RENEWAL_PILLS = {
  far:         { label: "Far out",     type: "green", weight: "soft" },
  approaching: { label: "Approaching", type: "amber", weight: "soft" },
  past:        { label: "Past",        type: "red",   weight: "heavy" },
  // A lapsed ongoing customer: terminal status, not a one-off. Same red as
  // `past` because the urgency is the same, different word because the
  // reason is — the contract is marked over, not merely run out.
  lapsed:      { label: "Lapsed",      type: "red",   weight: "heavy" },
};

/** What to render in the Renewal column.
 *
 *  Returns either {kind:"pill", label, type, weight} or {kind:"text", label}
 *  — never null, so the cell has one shape and the caller has no branch to
 *  get wrong.
 *
 *  TWO TEXT CASES, both deliberately colourless:
 *    done     "One Time · Done" — muted. The finished-one-timer exception.
 *    unknown  "—". No derivable term end, so there is no proximity to
 *             state; the row's own no_term_data tag says why.
 *
 *  The `done` case reads isFinishedOneTime(row) as its guard even though the
 *  server already answered: the helper is the single statement of the policy
 *  on this side (see its comment), and the guard test asserts the two agree
 *  row for row. */
export function renewalPill(row) {
  if (isFinishedOneTime(row) || row?.renewal_state === "done") {
    return { kind: "text", label: "One Time · Done", muted: true };
  }
  const pill = RENEWAL_PILLS[row?.renewal_state];
  if (!pill) return { kind: "text", label: "—", muted: true };
  return { kind: "pill", ...pill };
}

/** The tooltip on the Renewal cell: the day count, in words, plus what the
 *  amber threshold currently is — read from meta, never hardcoded. */
export function renewalTitle(row, meta, fmtDate) {
  const days = row?.days_to_term_end;
  const ended = row?.expiration_date
    ? (fmtDate ? fmtDate(row.expiration_date) : row.expiration_date)
    : null;
  if (isFinishedOneTime(row) || row?.renewal_state === "done") {
    return "A One Time engagement that has been delivered — nothing to renew."
      + (ended ? ` Recorded end: ${ended}.` : "");
  }
  if (row?.renewal_state === "lapsed") {
    // NAMES THE RECORDED END, because the lapsed rows are ordered by it
    // (most recently ended first) and a reader asking "why is this row
    // above that one" needs the answer in reach. It is the STORED value —
    // these rows have no derivable term at all, which is exactly why the
    // sort falls back to it.
    return `Contract status ${row?.contract_status || "terminal"} — this `
      + "contract is marked over"
      + (ended ? `, recorded end ${ended}` : "")
      + ". If it renewed, the term fields need updating in CRM.";
  }
  if (!Number.isInteger(days)) {
    return "No derivable term end — Term Start Date and Duration are what "
      + "this is computed from.";
  }
  const window = meta?.renewal_approaching_days ?? 90;
  if (days <= 0) {
    const past = -days;
    return `The derived term ended ${past} day${past === 1 ? "" : "s"} ago.`;
  }
  return `${days} day${days === 1 ? "" : "s"} of the derived term remain `
    + `(amber inside ${window}).`;
}

export function renewalWorkItem(row, fmtDate) {
  const state = row?.renewal_work_state;
  const labels = {
    activation_due: "Activation due",
    non_renewal_due: "Non-renewal due",
    scheduled: "Renewal scheduled",
    non_renewal_scheduled: "Non-renewal scheduled",
  };
  if (!Object.prototype.hasOwnProperty.call(labels, state)) return null;
  const date = row?.renewal_work_date;
  const when = date ? (fmtDate ? fmtDate(date) : date) : null;
  const base = row?.renewal_work_label || labels[state];
  const due = state === "activation_due" || state === "non_renewal_due";
  return {
    state,
    label: when ? `${base} ${when}` : base,
    type: due ? "red" : "blue",
    weight: due ? "heavy" : "soft",
    title: when
      ? `${base} on ${when}. Open Record Renewal to review it.`
      : `${base}. Open Record Renewal to review it.`,
  };
}

export const MGMT_CONTRACT_ACTION_ACTIVATION_DUE = "activation_due";
export const MGMT_CONTRACT_ACTION_NON_RENEWAL_DUE = "non_renewal_due";
export const MGMT_CONTRACT_ACTION_SCHEDULED = "scheduled";
export const MGMT_CONTRACT_ACTION_NON_RENEWAL_SCHEDULED = "non_renewal_scheduled";
export const MGMT_CONTRACT_ACTIONS = [
  MGMT_CONTRACT_ACTION_ACTIVATION_DUE,
  MGMT_CONTRACT_ACTION_NON_RENEWAL_DUE,
  MGMT_CONTRACT_ACTION_SCHEDULED,
  MGMT_CONTRACT_ACTION_NON_RENEWAL_SCHEDULED,
];

const MGMT_CONTRACT_ACTION_CARD_COPY = {
  [MGMT_CONTRACT_ACTION_ACTIVATION_DUE]: {
    label: "Activation due",
    tone: "red",
    weight: "heavy",
  },
  [MGMT_CONTRACT_ACTION_NON_RENEWAL_DUE]: {
    label: "Non-renewal due",
    tone: "red",
    weight: "heavy",
  },
  [MGMT_CONTRACT_ACTION_SCHEDULED]: {
    label: "Renewal scheduled",
    tone: "blue",
    weight: "soft",
  },
  [MGMT_CONTRACT_ACTION_NON_RENEWAL_SCHEDULED]: {
    label: "Non-renewal scheduled",
    tone: "blue",
    weight: "soft",
  },
};

export function contractActionRows(rows, action) {
  return (rows || []).filter(row => row?.renewal_work_state === action);
}

export function contractActionCounts(rows) {
  const counts = Object.fromEntries(
    MGMT_CONTRACT_ACTIONS.map(action => [action, 0]),
  );
  for (const row of rows || []) {
    if (Object.prototype.hasOwnProperty.call(counts, row?.renewal_work_state)) {
      counts[row.renewal_work_state] += 1;
    }
  }
  return counts;
}

export function filterContractActionRows(rows, activeAction) {
  return MGMT_CONTRACT_ACTIONS.includes(activeAction)
    ? contractActionRows(rows, activeAction)
    : (rows || []);
}

export function contractActionCards(counts, activeAction) {
  const actionCounts = counts || {};
  return MGMT_CONTRACT_ACTIONS.map(value => ({
    value,
    ...MGMT_CONTRACT_ACTION_CARD_COPY[value],
    count: actionCounts[value] || 0,
  }));
}

export function activationDueRows(rows) {
  return contractActionRows(rows, MGMT_CONTRACT_ACTION_ACTIVATION_DUE);
}

export function activationDueRowCount(rows) {
  return contractActionCounts(rows)[MGMT_CONTRACT_ACTION_ACTIVATION_DUE];
}

export function filterActivationDueRows(rows, active) {
  return active
    ? contractActionRows(rows, MGMT_CONTRACT_ACTION_ACTIVATION_DUE)
    : (rows || []);
}

/** The Term column's middle: "start → derived end (last covered day)".
 *  Null when there is no derivable term, so the cell falls back to "—" and
 *  the row's tag carries the explanation. */
export function termRangeLabel(row, fmtDate) {
  const start = row?.term_start || row?.term_start_date;
  const end = row?.term_end_display;
  const show = (v) => (v ? (fmtDate ? fmtDate(v) : v) : null);
  if (!start && !end) return null;
  if (!end) return show(start);
  if (!start) return show(end);
  return `${show(start)} → ${show(end)}`;
}

/** Hover detail for the Term column. Names BOTH ends explicitly, because
 *  the whole view turns on the reader being able to tell the derived end
 *  from the stored one — the mismatch tag is meaningless otherwise. */
export function termTitle(row, fmtDate) {
  const show = (v) => (v ? (fmtDate ? fmtDate(v) : v) : "not recorded");
  if (row?.contract_change_enrolled) {
    return `Current covered period: ${show(row?.term_start)} through `
      + `${show(row?.term_end_display)}. Original term anchor: `
      + `${show(row?.term_anchor_start)}. Contract changes can set an exact `
      + "last covered day, so this end is read from the current Contract State.";
  }
  const months = row?.duration_months;
  return "Derived from Term Start Date + Duration: "
    + `${show(row?.term_start || row?.term_start_date)} plus `
    + `${Number.isInteger(months) ? `${months} months` : "an unrecorded duration"}`
    + `, so the last covered day is ${show(row?.term_end_display)}. `
    + "This is the term the scheduling arithmetic uses; the Term End Date "
    + "column beside it is the value stored in CRM. "
    // TERM NUMBER LIVES HERE NOW (2026-08-11). It came out of the table
    // because the row was cut off on the right at normal widths, and this
    // is where it belongs: it is an INPUT to the derivation the rest of
    // this tooltip explains — the contract year the current term began in,
    // which is what offsets the review window. A reader who needs it is
    // already reading this sentence.
    + `Term Start Year (the contract year this term began in): ${
        Number.isInteger(row?.term_number) ? row.term_number : "not recorded"}.`;
}

/** The merged Cycle cell: "Y3 · 2/4" — contract year, then the position
 *  against the year's allowance.
 *
 *  WHY IT IS MERGED (2026-08-11): the table was cutting off on the right at
 *  normal widths. CY and RN were two columns of one number each answering
 *  one question — where in the contract is this customer — so they became
 *  one column.
 *
 *  SAME on_cycle RULE AS cycleLabel, deliberately reused rather than
 *  re-derived: BOTH position fields must be populated, because half a
 *  position is not a position, and the write side (review_cycle.on_cycle)
 *  says the same. Off the cycle renders null so the cell falls back to "—".
 *
 *  The FORMAT differs from cycleLabel's "Y3 R2" on purpose: the allowance
 *  denominator is the thing Katelyn reads ("4 of 4 used" is why a row is
 *  awaiting renewal), and "Y3 R2 · 2/4" would say the review number twice.
 *  A null allowance renders "?" rather than a guessed 4 — the effective
 *  fallback is the server's, and the tooltip names it. */
export function managementCycleLabel(row) {
  if (row?.contract_change_enrolled) {
    const available = row?.governed_balance?.available_now;
    return !row?.governed_unavailable && Number.isSafeInteger(available) && available >= 0
      ? `${available} available now` : "Unavailable";
  }
  const year = row?.contract_year;
  const number = row?.review_number;
  if (row?.allowance_model === "banked") {
    if (year === null || year === undefined) return "Banked";
    return `Y${year} · Banked`;
  }
  if (year === null || year === undefined) return null;
  if (number === null || number === undefined) return null;
  const allowance = row?.reviews_per_year;
  return `Y${year} · ${number}/${
    allowance === null || allowance === undefined ? "?" : allowance}`;
}

/** Tooltip for the Cycle cell. States the allowance fallback when the
 *  column is null, because "?" on screen has to be answerable. */
export function managementCycleTitle(row, meta) {
  if (row?.contract_change_enrolled) {
    const current = row?.governed_balance;
    if (row?.governed_unavailable || !Number.isSafeInteger(current?.available_now)
        || current.available_now < 0) {
      return "Current contract allowance is unavailable. Staff review is required.";
    }
    return `${current.available_now} available now; ${current.available_unspent} `
      + `unspent; ${current.future_unreleased} release later. `
      + "The current Contract State controls booking and fulfillment.";
  }
  if (row?.allowance_model === "banked") {
    const year = row?.contract_year;
    return year === null || year === undefined
      ? "Banked allowance is date/window driven; no Legacy review position is shown."
      : `Contract year ${year} is date-derived. Banked allowance is controlled by review windows and completion history.`;
  }
  if (!managementCycleLabel(row)) {
    return "Not on a review cycle — Contract Year and Review Number are not "
      + "both recorded. One Time engagements are normally like this.";
  }
  const allowance = row?.reviews_per_year;
  if (allowance === null || allowance === undefined) {
    return `Contract year ${row.contract_year}, working toward review `
      + `${row.review_number}. Reviews Per Year is not recorded, so the cycle `
      + `falls back to ${meta?.default_reviews_per_year ?? 4}.`;
  }
  return `Contract year ${row.contract_year}, working toward review `
    + `${row.review_number} of ${allowance} for that year.`;
}

/** Auto Renew as a word. Null is a real state and reads "—": 236 rows are
 *  true and 57 false, so a blank means nobody has answered rather than no. */
export function autoRenewLabel(row) {
  if (row?.auto_renew === true) return "Yes";
  if (row?.auto_renew === false) return "No";
  return "—";
}

/** Badge tone for one problem tag.
 *
 *  COLOUR DISCIPLINE, because this cell sits next to two other coloured
 *  channels and stealing either would break both:
 *    red    is renewal urgency (the pill beside it)
 *    amber  means "blocked, waiting on something outside our control" — the
 *           workflow-status register's single meaning, and a record defect is
 *           not that; it is squarely inside our control
 *  so a defect takes ORANGE, the severity palette's medium/pending tone.
 *
 *  TWO REFINEMENTS, both to keep the colour honest:
 *    * only the LEADING tag is coloured. Rows are already ordered by their
 *      worst predicate, so a three-tag row lighting up three times would
 *      out-shout a one-tag row that is genuinely more urgent.
 *    * awaiting_renewal is NEVER orange, even leading. It is not a data
 *      error — the row is correct and waiting on a commercial event, and it
 *      is the largest population by far. Painting it as a defect would teach
 *      the reader that the colour means nothing. */
export function problemTagTone(name, isFirst) {
  if (!isFirst) return "slate";
  return name === "awaiting_renewal" ? "slate" : "orange";
}

// ── The facet cards (2026-08-11) ─────────────────────────────────────
// Three groups above the table — contract status, tier, auto renew —
// reusing the Scheduling view's severity-card chassis: count, small label,
// clickable, aria-pressed, click again to clear.
//
// THE IDENTITY RULE (#532/#534) IS THE WHOLE DESIGN CONSTRAINT: every
// card's number must equal the row count the list shows when that card
// alone is active. It holds by construction here, and only because of two
// decisions:
//
//   1. ONE HELPER counts and one helper filters, over THE SAME rows the
//      table renders — after search and the needs-fixing filter, before
//      any card filter. A second derivation anywhere is how the number on
//      a control stops matching the list it opens.
//   2. NOTHING IS EVER FOLDED. Closed is its own card, never added into
//      Expired's: a card whose number covers two statuses would open a
//      list containing rows the card does not name, which is exactly the
//      defect the identity rule exists to prevent.
//
// Groups COMPOSE (Gold ∩ Ongoing ∩ auto-renew-blank) and each group holds
// at most one active value, so the composition is always an intersection
// and never an ambiguous union.

export const MGMT_FACET_STATUS = "status";
export const MGMT_FACET_TIER = "tier";
export const MGMT_FACET_AUTORENEW = "autorenew";
// The lifecycle phase (2026-08-14, SPEC-customer-lifecycle-phases Layer 2)
// — a fourth group in the machinery above, not a new invention.
export const MGMT_FACET_PHASE = "phase";
export const MGMT_FACETS = [
  MGMT_FACET_STATUS, MGMT_FACET_TIER, MGMT_FACET_AUTORENEW, MGMT_FACET_PHASE,
];

/** The sentinel for "the column is blank". A real key, not null, because it
 *  is a REAL POPULATION a reader filters to — "nobody has asked this
 *  customer" is the worklist, not the absence of one. */
export const MGMT_FACET_UNSET = "__unset__";

/** Auto renew as a facet value: yes / no / unset. Three states, because the
 *  column has three and collapsing the blank into "no" would invent a
 *  commercial fact (autoRenewLabel makes the same distinction on screen). */
export function autoRenewFacet(row) {
  if (row?.auto_renew === true) return "yes";
  if (row?.auto_renew === false) return "no";
  return MGMT_FACET_UNSET;
}

/** A row's value in one facet group. The WIRE label for status (so the
 *  filter agrees with the payload) even though the chip may render "One
 *  Time" — the display rename lives in contractDisplayLabel alone. */
export function managementFacetValue(row, group) {
  if (group === MGMT_FACET_STATUS) return row?.contract_status || MGMT_FACET_UNSET;
  if (group === MGMT_FACET_TIER) return row?.tier || MGMT_FACET_UNSET;
  if (group === MGMT_FACET_AUTORENEW) return autoRenewFacet(row);
  // READ, NEVER DERIVED. The phase is computed once, server-side
  // (backend/shared/customer_management.py, lifecycle_phase) from the
  // contract status, the renewal state, the cycle position and the
  // workflow status — three of which this row does not even carry. A
  // second derivation here is how a card and a row start disagreeing
  // about the same customer.
  //
  // A row from a payload older than this build has no phase and falls to
  // the unset sentinel, which renders as its own card (the "a value this
  // build has never heard of" rule below) rather than vanishing from a
  // group whose counts must still sum to the fleet.
  if (group === MGMT_FACET_PHASE) return row?.lifecycle_phase || MGMT_FACET_UNSET;
  return MGMT_FACET_UNSET;
}

/** {group: {value: count}} over the given rows — the ONE count every card
 *  in every group reads. Not zero-filled: a value with no rows has no key,
 *  and the caller decides which cards render (see MGMT_STATUS_CARD_ORDER). */
export function managementFacetCounts(rows) {
  const counts = { [MGMT_FACET_STATUS]: {}, [MGMT_FACET_TIER]: {},
                   [MGMT_FACET_AUTORENEW]: {}, [MGMT_FACET_PHASE]: {} };
  for (const row of rows || []) {
    if (!row) continue;
    for (const group of MGMT_FACETS) {
      const value = managementFacetValue(row, group);
      counts[group][value] = (counts[group][value] || 0) + 1;
    }
  }
  return counts;
}

/** Apply the active facets — an intersection, one value per group.
 *  `active` is {status, tier, autorenew}; a falsy value means no filter for
 *  that group, so the unfiltered call returns everything. */
export function filterManagementFacets(rows, active) {
  return (rows || []).filter(row => MGMT_FACETS.every(group => {
    const want = active?.[group];
    if (!want) return true;
    return managementFacetValue(row, group) === want;
  }));
}

/** Card ORDER per group, and the labels. Status runs in contract-lifecycle
 *  order rather than alphabetically, so the row of cards reads as a
 *  progression; tier runs cheapest-first, matching how the tiers are always
 *  listed elsewhere. */
export const MGMT_STATUS_CARD_ORDER = [
  "Ongoing", "Open", "On Hold", "Pending", "New", "Expired", "Closed",
];
export const MGMT_TIER_CARD_ORDER = ["Bronze", "Silver", "Gold", "Platinum"];
export const MGMT_AUTORENEW_CARD_ORDER = ["yes", "no", MGMT_FACET_UNSET];

/** The five phases in JOURNEY order — where a customer is in their life
 *  with us, read left to right. THE MIRRORING GUARD (KNOWN_CONTRACT_STATUS
 *  pattern): these keys and this order mirror customer_management.
 *  PHASE_ORDER in the backend, which also ships them as meta.phase_order.
 *  Pinned whole on both sides, so adding a phase to one is a red test on
 *  the other rather than a card that never renders. */
export const MGMT_PHASE_CARD_ORDER = [
  "onboarding", "first_review", "normal_cycle", "end_nearing", "offboarding",
];

export const MGMT_AUTORENEW_LABELS = {
  yes: "Auto renew",
  no: "No auto renew",
  [MGMT_FACET_UNSET]: "Not answered",
};

/** The reader's words for each phase. The wire keys above are what the
 *  filter and the payload agree on; these are what the card says (the
 *  contractDisplayLabel split — one statement of the display name). */
export const MGMT_PHASE_LABELS = {
  onboarding: "Onboarding",
  first_review: "First Review",
  normal_cycle: "Normal Cycle",
  end_nearing: "End Nearing",
  offboarding: "Offboarding",
  [MGMT_FACET_UNSET]: "No phase",
};

/** The cards for one group, in order, each {group, value, label, count,
 *  sub}. THE RENDER RULES, and each earns its place:
 *
 *   * a value with rows renders;
 *   * a value with NO rows does not — the status vocabulary is seven wide
 *     and several are permanently empty (Pending has had zero rows since
 *     the universe rule landed), so a zero card there is noise, not news.
 *     This is the PillToggle badge idiom rather than the severity cards'
 *     zero-fill: those bands are a three-value vocabulary where "nothing is
 *     Critical" is the answer to a question somebody asked;
 *   * the ACTIVE value always renders, even at zero. A search that empties
 *     the active card must not delete the control that is filtering the
 *     list — an invisible active filter is the worst state this can reach;
 *   * every Auto renew card always renders. It is a complete three-way
 *     partition of the fleet, and a zero on "Not answered" is the FINISHED
 *     STATE of a worklist — the one zero here worth seeing.
 */
export function managementFacetCards(group, counts, activeValue) {
  const groupCounts = counts?.[group] || {};
  const order = group === MGMT_FACET_STATUS ? MGMT_STATUS_CARD_ORDER
    : group === MGMT_FACET_TIER ? MGMT_TIER_CARD_ORDER
      : group === MGMT_FACET_PHASE ? MGMT_PHASE_CARD_ORDER
        : MGMT_AUTORENEW_CARD_ORDER;
  // EVERY PHASE CARD ALWAYS RENDERS, for the Auto renew reason: the five
  // are a COMPLETE PARTITION of the fleet, so "0 Onboarding" is an answer
  // to the question the group exists to ask ("how many are onboarding?")
  // rather than the absence of one. A group whose cards came and went
  // would also make the five-sum-to-the-total reading impossible to
  // check by eye, which is the reading (#532) the whole design turns on.
  const always = group === MGMT_FACET_AUTORENEW || group === MGMT_FACET_PHASE;
  // A value present in the data but absent from the order list (a tier or
  // status this build has never heard of) still gets a card, after the known
  // ones: an unrenderable population would be invisible in every group at
  // once, which is how a fleet-wide count silently stops adding up.
  const extras = Object.keys(groupCounts).filter(v => !order.includes(v));
  return [...order, ...extras.sort()]
    .map(value => ({
      group,
      value,
      label: group === MGMT_FACET_AUTORENEW
        ? MGMT_AUTORENEW_LABELS[value] || value
        : group === MGMT_FACET_PHASE
          ? MGMT_PHASE_LABELS[value] || value
          : value === MGMT_FACET_UNSET ? "Not set"
            : contractDisplayLabel(value),
      count: groupCounts[value] || 0,
      // Phase cards are UNCOLOURED, like Tier: a phase is a lens, not a
      // state — the contract-status row already owns the colour on this
      // screen, and a second coloured row would compete with it for the
      // same glance. (Zero new colour values anywhere in this build:
      // SPEC-customer-lifecycle-phases, "What must never happen".)
      tone: group === MGMT_FACET_STATUS
        ? (CONTRACT_STATUS_TONES[value] || "slate") : null,
    }))
    .filter(card => always || card.count > 0 || card.value === activeValue);
}

// ── The Sort control (2026-08-11) ────────────────────────────────────
// The rowSortOptions idiom, and the DEFAULT IS THE SERVER ORDER: the
// severity + renewal-proximity triage the whole view is built around.
//
// A chosen sort applies to the WHOLE list, so problem rows interleave with
// clean ones. That is deliberate and it is why the default is named for what
// it does: the triage order is the answer to "what should I fix", and
// "sort by tier" is a different question the reader has explicitly asked.
// THE ONE STATEMENT OF THE DISPLAY NAME (CONTENT-01 pattern). Dataverse
// calls the column pc_patriotaccountexecutive and the wire key stays
// account_executive / customer_manager — those legacy literals are never
// renamed. The PORTAL says "Customer Manager", everywhere, and it says it
// from here: a second literal in a JSX label or a sort option is how the
// two surfaces start disagreeing. Guard-tested.
export const CUSTOMER_MANAGER_LABEL = "Customer Manager";

export const MGMT_SORT_DEFAULT = "";
export const MGMT_SORT_CUSTOMER = "customer_name";
export const MGMT_SORT_TIER = "tier";
export const MGMT_SORT_TERM_END = "term_end";
// Wire key, not display text — see CUSTOMER_MANAGER_LABEL above.
export const MGMT_SORT_MANAGER = "customer_manager";
export const MGMT_SORT_KEYS = [
  MGMT_SORT_DEFAULT, MGMT_SORT_CUSTOMER, MGMT_SORT_TIER, MGMT_SORT_TERM_END,
  MGMT_SORT_MANAGER,
];
/** localStorage view key for loadRowSort/saveRowSort. */
export const MGMT_SORT_VIEW = "management";

export function managementSortOptions() {
  return [
    { value: MGMT_SORT_DEFAULT, label: "Default (needs fixing first)" },
    { value: MGMT_SORT_CUSTOMER, label: "Customer name (A–Z)" },
    { value: MGMT_SORT_TIER, label: "Tier (highest first)" },
    { value: MGMT_SORT_TERM_END, label: "Soonest term end" },
    // A–Z like the customer-name option, and for the same reason: the
    // question behind it is "show me my book of business", not a ranking.
    { value: MGMT_SORT_MANAGER, label: `${CUSTOMER_MANAGER_LABEL} (A–Z)` },
  ];
}

/** COERCED on the way in and out, the schedulingFilter lesson: a stored
 *  value is user-writable and outlives the release that wrote it. */
export function coerceManagementSort(value) {
  return MGMT_SORT_KEYS.includes(value) ? value : MGMT_SORT_DEFAULT;
}

/** Tier rank for the sort: HIGHEST FIRST, because the question behind
 *  sorting a renewal worklist by tier is "which of these matters most
 *  commercially". An unset tier sorts last — it is not a low tier, it is an
 *  unknown one. */
const MGMT_TIER_SORT_RANK = { Platinum: 0, Gold: 1, Silver: 2, Bronze: 3 };

export function sortManagementRows(rows, sortKey) {
  const list = [...(rows || [])].filter(Boolean);
  if (!sortKey) return list;              // the server's triage order
  const name = (r) => lower(r?.customer_name);
  const rank = (r) => {
    if (sortKey === MGMT_SORT_TIER) {
      return MGMT_TIER_SORT_RANK[r?.tier] ?? 9;
    }
    if (sortKey === MGMT_SORT_TERM_END) {
      // days_to_term_end is the DERIVED count and the honest first choice;
      // renewal_sort_days is its stored-expiration fallback, which exists so
      // the terminal population is orderable at all (see the backend's
      // sort_key and its ADR-0086 D1 note — ordering a worklist is the
      // display-and-comparison role that column already has). Rows with
      // neither sort last rather than claiming a position.
      const days = Number.isInteger(r?.days_to_term_end)
        ? r.days_to_term_end
        : (Number.isInteger(r?.renewal_sort_days) ? r.renewal_sort_days : null);
      return days === null ? Number.MAX_SAFE_INTEGER : days;
    }
    if (sortKey === MGMT_SORT_MANAGER) {
      // UNASSIGNED LAST, and as a BLOCK: an absent manager is not a name
      // that happens to sort before "Aaron", it is a gap in the CRM. Two
      // ranks rather than a name comparison on "" — so every unassigned row
      // lands together at the bottom, in customer order, where a reader
      // looking for the gaps can see how many there are.
      return lower(r?.customer_manager) ? 0 : 1;
    }
    return 0;
  };
  list.sort((a, b) => {
    if (sortKey !== MGMT_SORT_CUSTOMER) {
      const ra = rank(a);
      const rb = rank(b);
      if (ra !== rb) return ra - rb;
    }
    // The manager's OWN name, ahead of the customer tiebreak below — rank()
    // has already pushed the unassigned block to the bottom, so this only
    // ever compares two assigned rows, and each manager's customers arrive
    // as one contiguous run.
    if (sortKey === MGMT_SORT_MANAGER) {
      const ma = lower(a?.customer_manager);
      const mb = lower(b?.customer_manager);
      if (ma !== mb) return ma.localeCompare(mb);
    }
    // Name is one option's sort and every other option's stable tiebreak, so
    // equal keys never shuffle between renders.
    const na = name(a);
    const nb = name(b);
    if (na !== nb) return na.localeCompare(nb);
    return lower(a?.slug).localeCompare(lower(b?.slug));
  });
  return list;
}
